import React, { useState, useEffect, useRef } from "react";
import { Col, Container, Row, } from "reactstrap";
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { FileUpload } from 'primereact/fileupload';
import { Chips } from 'primereact/chips';
import { InputText } from 'primereact/inputtext';
import { Editor } from 'primereact/editor';
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { CascadeSelect } from 'primereact/cascadeselect';
import { Button } from "primereact/button";
import { Dialog } from 'primereact/dialog';
import axios from "axios";
import Swal from "sweetalert2";
import { getBase64 } from "utils/CommonFunctions";
import { Toast } from "primereact/toast"
import { ChevronDownIcon } from "primereact/icons/chevrondown"
import { ChevronRightIcon } from "primereact/icons/chevronright"
import { deliveryErrorMessage, isSuccessfulDelivery } from "../../utils/deliveryResult"

const emailSignatureRequestCache = new Map();

const normalizeEmailStatus = value => String(value || "").toLowerCase().replace(/[_-]+/g, " ").trim();
const isEmailFailed = record => /smtp failed|smtp failure|smtp error|failed|error|bounced|rejected/.test(
    normalizeEmailStatus(record?.email_status || record?.status)
) || Boolean(record?.email_error || record?.failure_reason);

const getTaskFailureDetails = taskData => {
    const result = taskData?.result;
    const resultData = result && typeof result === "object" ? result : {};
    const failedEmail = Array.isArray(resultData.failed_emails) ? resultData.failed_emails[0] : null;
    const rawMessage = failedEmail?.error || resultData.email_error || taskData?.email_error || taskData?.error ||
        resultData.error || resultData.message || taskData?.message ||
        (typeof result === "string" ? result : "Email delivery failed.");
    return {
        fromAddress: resultData.from_address || resultData.from_email || taskData?.from_address || taskData?.from_email || "",
        recipient: failedEmail?.email || failedEmail?.recipient || "",
        message: typeof rawMessage === "string" ? rawMessage : rawMessage?.message || rawMessage?.detail || "Email delivery failed.",
    };
};

const waitForEmailTask = async taskId => {
    if (!taskId) {
        throw new Error("The email task was not created.");
    }

    for (let attempt = 0; attempt < 40; attempt += 1) {
        const response = await axios.get(`/api/v1/task-status/${encodeURIComponent(taskId)}/`);
        const taskData = response?.data || {};
        const status = String(taskData?.status || taskData?.task_status || "").toUpperCase();
        const result = taskData?.result;
        const resultData = result && typeof result === "object" ? result : {};
        const resultStatus = normalizeEmailStatus(resultData.email_status || resultData.delivery_status || resultData.status);
        const hasFailedEmails = Array.isArray(resultData.failed_emails) && resultData.failed_emails.length > 0;

        if (["FAILURE", "FAILED", "REVOKED"].includes(status) || isEmailFailed(taskData) || isEmailFailed(resultData) || hasFailedEmails) {
            return { status: "FAILED", taskData };
        }

        if (status === "SUCCESS") {
            return { status: resultStatus && !["sent", "success", "completed"].includes(resultStatus) ? "FAILED" : "SUCCESS", taskData };
        }

        await new Promise(resolve => setTimeout(resolve, 1500));
    }

    return { status: "TIMEOUT", taskData: null };
};

const protectEmailEditorCleanup = editor => {
    const core = editor.core;
    const context = core.context;
    const element = context.element;
    const destroy = editor.destroy.bind(editor);
    editor.destroy = () => {
        // SunEditor leaves resource and selection callbacks queued after destruction.
        element.wysiwyg.classList.add("se-read-only");
        destroy();
        context.element = element;
        core._iframeAutoHeight = () => {};
        core._checkPlaceholder = () => {};
    };
};

const emailLookupCache = {
    templates: undefined,
    signatures: undefined,
    fromEmails: undefined,
    primaryFromEmail: undefined,
};

const emailLookupPromises = {
    templates: null,
    signatures: null,
    fromEmails: null,
    primaryFromEmail: null,
};

const candidateLookupCache = {
    key: "",
    rows: [],
    totalRecords: 0,
};

const linkifyEditorHtml = value => {
    if (!value || typeof document === "undefined") return value || "";

    const wrapper = document.createElement("div");
    wrapper.innerHTML = value;
    const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

    const walk = node => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.nodeValue;
            if (!urlRegex.test(text)) return;

            urlRegex.lastIndex = 0;
            const fragment = document.createDocumentFragment();
            let lastIndex = 0;

            text.replace(urlRegex, (match, _unused, offset) => {
                fragment.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
                const href = match.startsWith("http") ? match : `https://${match}`;
                const anchor = document.createElement("a");
                anchor.href = href;
                anchor.target = "_blank";
                anchor.rel = "noopener noreferrer";
                anchor.textContent = match;
                anchor.style.color = "#0d6efd";
                anchor.style.textDecoration = "underline";
                fragment.appendChild(anchor);
                lastIndex = offset + match.length;
                return match;
            });

            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
            node.parentNode.replaceChild(fragment, node);
            return;
        }

        if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== "A") {
            Array.from(node.childNodes).forEach(walk);
        }
    };

    Array.from(wrapper.childNodes).forEach(walk);
    return wrapper.innerHTML;
};

const removeEditorBackgrounds = value => {
    if (!value) return value || "";

    if (typeof document === "undefined") {
        return value
            .replace(/\sbackground(?:-color)?\s*:\s*[^;"']+;?/gi, "")
            .replace(/\sbgcolor=(["']).*?\1/gi, "");
    }

    const wrapper = document.createElement("div");
    wrapper.innerHTML = value;

    wrapper.querySelectorAll("*").forEach(element => {
        element.style.background = "";
        element.style.backgroundColor = "";
        element.style.removeProperty("background");
        element.style.removeProperty("background-color");
        element.removeAttribute("bgcolor");

        if (!element.getAttribute("style")?.trim()) {
            element.removeAttribute("style");
        }
    });

    return wrapper.innerHTML;
};

const normalizeEmailSignatureHtml = value => {
    const html = value || "";
    if (!html.trim()) return "";

    if (typeof document === "undefined") {
        const normalized = html.replace(/<img\b([^>]*)>/gi, img => {
            if (/style\s*=/i.test(img)) {
                return img.replace(
                    /style=(["'])(.*?)\1/i,
                    (_match, quote, styleText) =>
                        `style=${quote}${styleText};max-width:220px;width:auto;height:auto;display:block;margin:6px 0;${quote}`
                );
            }
            return img.replace("<img", '<img style="max-width:220px;width:auto;height:auto;display:block;margin:6px 0;"');
        });
        return `<div class="email-signature-block">${normalized}</div>`;
    }

    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;

    wrapper.querySelectorAll("p").forEach(paragraph => {
        if (!paragraph.textContent.replace(/\u00a0/g, " ").trim() &&
            !paragraph.querySelector("img, table, video, iframe")) {
            paragraph.remove();
            return;
        }
        paragraph.style.margin = "0 0 4px";
        paragraph.style.lineHeight = "1.5";
    });
    wrapper.querySelectorAll("figure, .se-component").forEach(element => {
        element.style.border = "0";
        element.style.outline = "none";
        element.style.padding = "0";
        element.style.margin = "0";
    });

    wrapper.querySelectorAll("img").forEach(img => {
        img.style.maxWidth = "220px";
        img.style.width = "auto";
        img.style.height = "auto";
        img.style.display = "block";
        img.style.margin = "0";
        let previous = img.previousSibling;
        while (previous && (previous.nodeName === "BR" ||
            (previous.nodeType === 3 && !previous.textContent.trim()))) {
            const blank = previous;
            previous = previous.previousSibling;
            blank.remove();
        }
        img.removeAttribute("width");
        img.removeAttribute("height");
    });

    wrapper.querySelectorAll("table").forEach(table => {
        table.style.maxWidth = "100%";
        table.style.width = "auto";
    });

    return `<div class="email-signature-block">${wrapper.innerHTML}</div>`;
};

const getJobDescriptionContent = job => {
    const normalize = value => (Array.isArray(value)
        ? value.filter(Boolean).join("<br />") : String(value || "")).trim();
    const firstContent = values => values.map(normalize).find(Boolean) || "";
    const description = firstContent([
        job?.job_description, job?.jobDescription,
        job?.job_details?.job_description, job?.job_details?.jobDescription,
        job?.description, job?.job_details?.description,
    ]);
    const responsibilities = firstContent([
        job?.roles_responsibility, job?.rolesAndResponsibilities,
        job?.job_details?.roles_responsibility, job?.job_details?.rolesAndResponsibilities,
    ]);
    return [...new Set([description, responsibilities].filter(Boolean))].join("<br /><br />");
};

const escapeEmailHtml = value =>
    String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const formatJobDescriptionForEmail = value => {
    const plainText = String(value || "")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/\u00a0/g, " ")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<li\b[^>]*>/gi, "\n• ")
        .replace(/<\/(?:li|div|h[1-6])>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]+>/g, " ")
        .replace(/[ \t]+/g, " ")
        .replace(/\n\s+/g, "\n")
        .trim();

    if (!plainText) return "";

    const headings = [
        "Job Description",
        "Roles & Responsibilities",
        "Preferred Qualifications",
        "Position:",
        "Location:",
        "Shift Timings:",
        "Experience:",
        "Salary & Incentives:",
        "Work Culture:",
        "About the Role:",
        "Key Responsibilities:",
        "Candidate Requirements:",
        "What We Offer:",
        "Lead Generation:",
        "Client Engagement:",
        "Relationship Management:",
        "Sales Process Management:",
        "Collaboration:",
        "Sales Reporting:",
        "Market Research:",
        "Target Achievement:",
        "Customer Feedback:",
        "Continuous Learning:",
        "Skills:",
        "Shift Flexibility:",
        "Attitude:",
        "Competitive Salary:",
        "Work Environment:",
        "Career Growth:",
        "Training & Development:",
        "Recognition:",
    ];

    let formatted = escapeEmailHtml(plainText)
        .replace(/\r?\n/g, "<br />")
        .replace(/\s*([•])\s*/g, "<br />&bull; ")
        .replace(/\s+o\s+/g, "<br />&nbsp;&nbsp;- ");

    headings.forEach(heading => {
        const safeHeading = escapeEmailHtml(heading);
        formatted = formatted.replace(
            new RegExp(`\\s*${safeHeading.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "g"),
            `<br /><strong>${safeHeading}</strong> `
        );
    });

    return formatted
        .replace(/<br \/>&bull;\s*<br \/><strong>/g, "<br />&bull; <strong>")
        .replace(/<br \/>&nbsp;&nbsp;-\s*<br \/><strong>/g, "<br />&nbsp;&nbsp;- <strong>")
        .replace(/(<strong>Key Responsibilities:<\/strong>|<strong>Candidate Requirements:<\/strong>|<strong>What We Offer:<\/strong>)\s*<br \/>&bull;/g, "$1<br />&bull;")
        .replace(/\s+-\s+/g, "<br />&bull; ")
        .replace(/(<br \/>&bull;\s*)+/g, "<br />&bull; ")
        .replace(/^(<br \/>)+/, "")
        .trim();
};

const prepareOutgoingEmailHtml = value => {
    if (!value || typeof document === "undefined") return value || "";
    const wrapper = document.createElement("div");
    wrapper.innerHTML = value;
    wrapper.querySelectorAll("p, li, td, th").forEach(element => {
        element.style.fontFamily = "Arial, sans-serif";
        element.style.fontSize = "15px";
        element.style.lineHeight = "23px";
        if (element.tagName === "P" && /^Dear\s/i.test(element.textContent.trim())) {
            element.style.margin = "0 0 16px";
        }
    });
    wrapper.querySelectorAll("table").forEach(table => {
        if (table.closest(".email-signature-block")) return;
        table.setAttribute("border", "1");
        table.setAttribute("cellpadding", "0");
        table.setAttribute("cellspacing", "0");
        table.setAttribute("width", "100%");
        table.style.width = "100%";
        table.style.borderCollapse = "collapse";
        table.style.border = "1px solid #9fb7cf";
        table.querySelectorAll("td, th").forEach(cell => {
            cell.style.border = "1px solid #9fb7cf";
            cell.style.padding = "9px 11px";
            cell.style.verticalAlign = "top";
            if (cell.cellIndex === 0) {
                cell.style.fontWeight = "700";
                cell.style.color = "#0f3f6e";
            }
        });
    });
    wrapper.querySelectorAll("span, font").forEach(element => {
        element.style.fontSize = "15px";
        element.removeAttribute("size");
    });
    // Fixed-height spacer cells survive email clients that discard paragraph margins.
    const insertSpacerBefore = (element, height) => {
        const spacer = document.createElement("table");
        spacer.setAttribute("role", "presentation");
        spacer.setAttribute("border", "0");
        spacer.setAttribute("cellpadding", "0");
        spacer.setAttribute("cellspacing", "0");
        spacer.setAttribute("width", "100%");
        spacer.style.cssText = "width:100%;border:0;border-collapse:collapse;";
        spacer.innerHTML = `<tbody><tr><td height="${height}" style="height:${height}px;border:0;padding:0;font-size:1px;line-height:${height}px;mso-line-height-rule:exactly;">&nbsp;</td></tr></tbody>`;
        element.before(spacer);
    };
    const jobTable = Array.from(wrapper.querySelectorAll("table")).find(table =>
        !table.closest(".email-signature-block") && /Company Name/i.test(table.textContent)
    );
    if (jobTable) insertSpacerBefore(jobTable, 16);
    wrapper.querySelectorAll("p").forEach(paragraph => {
        if (/^Roles and Responsibilities:?$/i.test(paragraph.textContent.trim())) {
            insertSpacerBefore(paragraph, 20);
            if (paragraph.nextElementSibling) insertSpacerBefore(paragraph.nextElementSibling, 12);
        }
    });
    return `<div style="font-family:Arial,sans-serif;font-size:15px;line-height:23px;color:#111827;">${wrapper.innerHTML}</div>`;
};

const buildEmailInfoRow = (label, value) => {
    if (!value) return "";

    return `
        <tr>
            <td width="30%" style="width: 30%; padding: 9px 11px; border: 1px solid #9fb7cf; background-color: #f3f8fc; vertical-align: top; font-family: Arial, sans-serif; font-size: 14px; line-height: 20px; font-weight: 700; color: #0f3f6e;">
                <strong>${escapeEmailHtml(label)}</strong>
            </td>
            <td style="padding: 9px 11px; border: 1px solid #9fb7cf; vertical-align: top; font-family: Arial, sans-serif; font-size: 14px; line-height: 20px; color: #111827;">
                ${escapeEmailHtml(value)}
            </td>
        </tr>
    `;
};
// Add this helper inside EmailAC component
const renderAttachmentName = (file, onPreview) => {
  const isUrl = typeof file.file_data === 'string' &&
    (file.file_data.startsWith('http://') || file.file_data.startsWith('https://'));

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onPreview(file);
  };

  return (
    <span
      title={file.file_name}
      style={{ color: "#0d6efd", textDecoration: "underline", cursor: "pointer" }}
      onClick={handleClick}
    >
      {file.file_name}
    </span>
  );
};
const loadCachedLookup = async (key, fetcher) => {
    if (emailLookupCache[key] !== undefined) {
        return emailLookupCache[key];
    }

    if (!emailLookupPromises[key]) {
        emailLookupPromises[key] = fetcher()
            .then((result) => {
                emailLookupCache[key] = result;
                return result;
            })
            .finally(() => {
                emailLookupPromises[key] = null;
            });
    }

    return emailLookupPromises[key];
};


const EmailAC = ({ selectedData, jobData, showDropdown = true, buttonLabel = "Mail", buttonClassName = "", buttonStyle = {}, onClose, onBeforeOpen, openRequest, onOpenRequestHandled }) => {
    const emailHtmlEditorRef = useRef(null);
    const applyingGeneratedEmail = useRef(false);
    const userInfo = JSON.parse(sessionStorage.getItem("user-info"));
    const atsUserInfo = JSON.parse(sessionStorage.getItem("user-info") || "{}");
    const loggedInUserId =
        userInfo?.user_details?.user_id ||
        userInfo?.user_id ||
        atsUserInfo?.user_details?.user_id ||
        atsUserInfo?.user_id ||
        "";
    const userEmail = userInfo?.user_details?.email;
    const selectedCompany = {
        company_id:
            jobData?.company_id ??
            jobData?.company?.company_id ??
            jobData?.company?.id ??
            userInfo?.user_details?.company_id ??
            null,
    };
    const selectedCompanyId = selectedCompany.company_id;
    const [canto, setCanto] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [smtpMissing, setSmtpMissing] = useState(false);
    const guardSmtp = () => {
    if (smtpMissing) {
        Swal.fire({
            icon: "warning",
            title: "SMTP Not Configured",
            html: `Your email account (<b>${userEmail || "logged-in user"}</b>) is not found in the SMTP list.<br/><br/>Please configure your SMTP account before sending emails.`,
            confirmButtonText: "OK",
        });
        return true; 
    }
    return false;
};
    const [lazyParams, setLazyParams] = useState({ page: 0, rows: 10 });
    const [formErrors, setFormErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const fileUploadRef = useRef(null);
    const toast = useRef(null)
    const [isDefaultName, setIsDefaultName] = useState();
    const [primaryFromEmail, setPrimaryFromEmail] = useState(null);
    const [jobs, setJobs] = useState([]);
const [jobsTotalRecords, setJobsTotalRecords] = useState(0);
const [jobsLazyParams, setJobsLazyParams] = useState({ page: 0, rows: 5 });
const [jobsSearchText, setJobsSearchText] = useState("");
    const [jobsLoading, setJobsLoading] = useState(false);
    const [candidateLoading, setCandidateLoading] = useState(false);
    const [showNoSubjectConfirm, setShowNoSubjectConfirm] = useState(false);
    const [showSendConfirmModal, setShowSendConfirmModal] = useState(false); 
    const [includeJD, setIncludeJD] = useState(false);
    const [hideJdAttachmentChip, setHideJdAttachmentChip] = useState(false);
const [pendingSendType, setPendingSendType] = useState(null);

    const base_url = process.env.REACT_APP_URL;


    // interview

    const [primarySkillsvalu, setprimarySkillsvalu] = useState([])
    const getprimary = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_CONDIDATES}/api/v1/skills/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            if (response.data && response.data.results) {
                const treeData = response.data.results

                setprimarySkillsvalu(treeData)
            }
        } catch (error) { }
    }

const handleSendMail = () => {
    if (!subject || subject.trim() === "") {
        setShowNoSubjectConfirm(true);
        return;
    }
    // sendNewMail();
    setShowSendConfirmModal(true);
};

const handleConfirmSend = () => {
    setShowNoSubjectConfirm(false);
    // sendNewMail();
      setShowSendConfirmModal(true)
};
const handleCancelSend = () => {
    setShowNoSubjectConfirm(false);
};


    //lokesh
    // Keep the default signature text in sync with the default item returned by the list API.
    const [defaultSignature, setDefaultSignature] = useState("");

    // Fetch default signature from API
    // const fetchDefaultSignature = async () => {
    //     try {
    //         const response = await axios.get(`/api/v1/email-signatures/default/`);
    //         if (response.data && response.data[0].signature_text) {
    //             setDefaultSignature(response.data[0].signature_text || " ");
    //             setIsDefaultName(response.data[0].is_default)
    //             // Use signature_text from response
    //         }
    //     } catch (error) {
    //         console.error("Error fetching default signature:", error);
    //         setDefaultSignature(""); // Fallback signature on error
    //     }
    // };

    // Build job info HTML for email template
    const getJobInfoHtml = () => {
      if (!jobData) return "";
      const jobTitle = jobData?.job_title || "Job Opportunity";
      const location = Array.isArray(jobData?.location) 
        ? jobData.location.join(", ") 
        : (jobData?.location || "");
      const jobType = jobData?.job_type || jobData?.employment_type || "";
      const salary = jobData?.salary || jobData?.salary_range || "";
      const skills = Array.isArray(jobData?.skills) 
        ? jobData.skills.join(", ") 
        : (jobData?.skills || "");
      const experience = `${jobData?.min_exp || ""} - ${jobData?.max_exp || ""} years`.replace(/^[\s\-]*|[\s\-]*$/g, "");

      return `
        <br />
        <div style="background-color: #f5f5f5; padding: 15px; margin: 15px 0; border-left: 4px solid #1a5fa8;">
          <h3 style="margin-top: 0; color: #1a5fa8; font-size: 16px;">${jobTitle}</h3>
          ${location ? `<p style="margin: 8px 0;"><strong>Location:</strong> ${location}</p>` : ""}
          ${jobType ? `<p style="margin: 8px 0;"><strong>Job Type:</strong> ${jobType}</p>` : ""}
          ${experience && experience !== "years" ? `<p style="margin: 8px 0;"><strong>Experience Required:</strong> ${experience}</p>` : ""}
          ${salary ? `<p style="margin: 8px 0;"><strong>Salary:</strong> ${salary}</p>` : ""}
          ${skills ? `<p style="margin: 8px 0;"><strong>Skills Required:</strong> ${skills}</p>` : ""}
        </div>
        <br />
      `;
    };

    const getJobCreatedByName = () => {
      if (!jobData) return "";
      if (jobData?.created_by_name) return jobData.created_by_name;
      if (Array.isArray(jobData?.recruiters_names) && jobData.recruiters_names.length) {
        return jobData.recruiters_names.join(", ");
      }
      return jobData?.recruiter_name || jobData?.created_by || "";
    };

const getLoggedInUserName = () => {
  const info = JSON.parse(sessionStorage.getItem("user-info") || "{}");
  const details = info?.user_details || info;
  return (
    [details?.first_name, details?.middle_name, details?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    details?.full_name ||
    details?.name ||
    details?.user_name ||
    details?.email ||
    ""
  );
};

const getJobDefaultSignature = () => {
  const loggedInName = getLoggedInUserName();
  return `
    <p>Thanks and Regards,</p>
    ${loggedInName ? `<p><strong>${loggedInName}</strong></p>` : ""}
  `;
};

    const getSelectedCandidateName = () => {
      if (!Array.isArray(selectedData) || selectedData.length !== 1) {
        return "";
      }

      const candidate = selectedData[0];
      return (
        [
          candidate?.first_name || candidate?.candidate_name || candidate?.name,
          candidate?.middle_name,
          candidate?.last_name,
        ]
          .filter(Boolean)
          .join(" ")
          .trim() || ""
      );
    };

    const getCandidateGreetingName = () =>
      getSelectedCandidateName() || "{{candidate_name}}";

    const [emailtextEditor, setEmailtextEditor] = useState(`
  <p style="margin:0 0 16px;">Dear <strong>${escapeEmailHtml(getCandidateGreetingName())}</strong>,</p>
  <br />
  ${defaultSignature || getJobDefaultSignature()}
    `)
    const selectedCandidateCount = Array.isArray(selectedData) ? selectedData.length : 0;
    const getJobCandidateIdsForEmails = (emails) => {
        if (!Array.isArray(selectedData) || !Array.isArray(emails)) return [];
        const emailSet = new Set(
            emails.map(e => (e || "").trim().toLowerCase()).filter(Boolean)
        );
        if (!emailSet.size) return [];
        return selectedData
            .filter(candidate => {
                const candidateEmail = (candidate?.email || candidate?.candidate_email || "")
                    .trim()
                    .toLowerCase();
                return candidateEmail && emailSet.has(candidateEmail);
            })
            .map(candidate => candidate?.job_candidate_id || candidate?.candidate_id || candidate?.id)
            .filter(Boolean);
    };
    const selectedJobCandidateIds = Array.isArray(selectedData)
        ? selectedData
            .map(candidate => candidate?.job_candidate_id || candidate?.candidate_id || candidate?.id)
            .filter(Boolean)
        : [];
//  useEffect(() => {
//         fetchDefaultSignature();
//     }, []);
const getGreeting = (type, selectedCandidates) => {
    // type: "all", "selected", "searched", "jobsallpopup", etc.
    if (
        type === "all" ||
        type === "jobsallpopup" ||
        type === "jobssearchedpopup" ||
        (Array.isArray(selectedCandidates) && selectedCandidates.length > 1)
    ) {
        return "Dear {{candidate_name}},";
    }
    if (Array.isArray(selectedCandidates) && selectedCandidates.length === 1) {
        return "Dear {{candidate_name}},";
    }
    return "Dear {{candidate_name}},";
};

    const [addCities, setaddCities] = useState([])

    const getcity = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_Jobs}/api/v1/cities/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )

            // Transform data if needed to match the Dropdown format
            const transformedData = response.data.results.map(city => ({
                name: city.name,
                code: city.city_id,
            }))
            setaddCities(transformedData)
        } catch (error) { }
    }

    // useEffect(() => {
    //     getcity()
    // }, [])

    // useEffect(() => {
    //     getprimary()
    // }, [])

const fetchJobs = async (page = 0, rows = 5) => {
  setJobsLoading(true);
  try {
    let endpoint = `/api/v1/jobs/?page=${page + 1}&size=${rows}&status=Approved / Open`;
    const response = await axios.get(endpoint);
    setJobs(response.data.results || []);
    setJobsTotalRecords(response.data.count || 0);
  } catch (error) {
    setJobs([]);
    setJobsTotalRecords(0);
  } finally {
    setJobsLoading(false);
  }
};

const onJobsPageChange = (event) => {
  setJobsLazyParams({ page: event.page, rows: event.rows });
};


    const getallactivecontacts = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_Company_Contact}/api/v1/contacts/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            const transformedData = response.data.results.map(city => ({
                name: city.first_name,
                id: city.contact_id,
            }))
            settypeInterviewcontact(transformedData)
        } catch (error) { }
    }
    const [interviewdroptype, setinterviewdroptype] = useState([])

    const getallinterviewdrop = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_Calendar}/api/v1/event-lookup/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            const data = response.data
            const types = data.Types.map(type => ({
                label: type,
            }))
            if (response.data === 500) {
            }

            setinterviewdroptype(types)
        } catch (error) { }
    }

    const getAllActive = async (page, size) => {
        setCandidateLoading(true);
        try {
            const endpoint = `/api/v1/candidates/?page=${page + 1}&size=${size}&status=Active`;
            const cacheKey = `${page}-${size}`;
            if (candidateLookupCache.key === cacheKey && candidateLookupCache.rows.length > 0) {
                setCanto(candidateLookupCache.rows);
                setTotalRecords(candidateLookupCache.totalRecords);
                return;
            }
            const response = await axios.get(endpoint);
            // const response = await axios.get(`/api/v1/candidates${selectedType ? '/' + selectedType : ''}/?${param ? param + '&' : ''}page=${page + 1}&size=${size}`,);
            if (response.status === 200) {
                const jobs = response.data.results || [];
                setCanto(jobs);
                setTotalRecords(response.data.count || 0)
                candidateLookupCache.key = cacheKey;
                candidateLookupCache.rows = jobs;
                candidateLookupCache.totalRecords = response.data.count || 0;

            } else {
                setCanto([]);
                setTotalRecords(0);
                candidateLookupCache.key = cacheKey;
                candidateLookupCache.rows = [];
                candidateLookupCache.totalRecords = 0;
            }

        } catch (error) {
            console.error("Failed to fetch candidates", error);
        } finally {
            setCandidateLoading(false);
        }
    };

    useEffect(() => {
      
        getallactivecontacts()
        getallinterviewdrop()

    }, [])

    // new email
    // dialog with footer

    const [privateChecked, setPrivateChecked] = useState(false);
    const [recipientPreviewVisible, setRecipientPreviewVisible] = useState(false);
    const [recipientPreviewList, setRecipientPreviewList] = useState([]);

    const footerContent = (
        <div className="d-flex w-100 align-items-center justify-content-center gap-3 flex-wrap">
            {/* Private option intentionally hidden for now. */}
            <Button className="preview-btn" onClick={handleSendMail} disabled={isLoading}>
                <i className="pi pi-send me-1"></i> {isLoading ? "Sending..." : "Send Email"}
            </Button>
        </div>
    )

    const [emailviewpop, SetEmailviewpop] = useState(false)

    const [emailviewpopSelected, SetEmailviewpopSelected] = useState(false)

    const [emailviewpopSearched, SetEmailviewpopSearched] = useState(false)

    const [emailviewpopAll, SetEmailviewpopAll] = useState(false)

    const prevSignatureRef = useRef(null);
    const emailPreviewRef = useRef(null);
    const [templateBody, setTemplateBody] = useState("");
    const [signatureText, setSignatureText] = useState("");
    const [signatureLoading, setSignatureLoading] = useState(false);
    const [signatureError, setSignatureError] = useState("");

    const normalizeMatchValue = value =>
        String(value || "").trim().toLowerCase();

    const compactMatchValue = value =>
        normalizeMatchValue(value).replace(/[^a-z0-9]/g, "");

    const getCompanyIdValue = source =>
        source?.company_id ??
        source?.company?.company_id ??
        source?.company?.id ??
        (typeof source?.company === "number" || typeof source?.company === "string"
            ? source.company
            : null);

    const getCompanyNameValue = source =>
        source?.company_name ||
        source?.companyName ||
        source?.company?.company_name ||
        source?.company?.name ||
        "";

    const getSignatureFromEmailValue = source =>
        source?.from_email ||
        source?.fromEmail ||
        source?.sender_email ||
        source?.senderEmail ||
        source?.email ||
        "";

    const getEmailDomainMatchValue = source => {
        const email = source?.code || source?.from_email || source?.email || "";
        const domain = String(email).split("@")[1] || "";
        return compactMatchValue(domain.split(".")[0] || domain);
    };

    const getSignificantCompanyWords = value =>
        normalizeMatchValue(value)
            .split(/[^a-z0-9]+/)
            .filter(
                word =>
                    word.length >= 4 &&
                    !["software", "services", "service", "solutions", "private", "limited", "digital", "media", "global"].includes(word)
            );

    const signatureMatchesEmailDomain = (option, signatureCompanyName, signatureLabel) => {
        const emailDomain = getEmailDomainMatchValue(option);
        const compactCompanyName = compactMatchValue(signatureCompanyName);
        const compactLabel = compactMatchValue(signatureLabel);
        const significantWords = [
            ...getSignificantCompanyWords(signatureCompanyName),
            ...getSignificantCompanyWords(signatureLabel),
        ];

        return (
            (compactCompanyName && emailDomain.includes(compactCompanyName)) ||
            (compactLabel && emailDomain.includes(compactLabel)) ||
            significantWords.some(word => emailDomain.includes(word))
        );
    };

    const applyFromEmailForSignature = (signatureOption, signatureDetails = {}) => {
        if (!Array.isArray(fromEmail) || fromEmail.length === 0) return;

        const signatureData = { ...(signatureOption || {}), ...(signatureDetails || {}) };
        const signatureFromEmail = normalizeMatchValue(getSignatureFromEmailValue(signatureData));
        const signatureCompanyId = getCompanyIdValue(signatureData);
        const signatureCompanyName = normalizeMatchValue(getCompanyNameValue(signatureData));
        const signatureLabel = normalizeMatchValue(signatureData.label || signatureData.name);
        const matchedFromEmail =
            (signatureFromEmail &&
                fromEmail.find(option =>
                    normalizeMatchValue(option.code || option.from_email) === signatureFromEmail
                )) ||
            (signatureCompanyId != null &&
                fromEmail.find(option =>
                    String(getCompanyIdValue(option)) === String(signatureCompanyId)
                )) ||
            (signatureCompanyName &&
                fromEmail.find(option =>
                    normalizeMatchValue(getCompanyNameValue(option)) === signatureCompanyName
                )) ||
            (signatureLabel &&
                fromEmail.find(option =>
                    normalizeMatchValue(getCompanyNameValue(option)) === signatureLabel
                )) ||
            fromEmail.find(option =>
                signatureMatchesEmailDomain(option, signatureCompanyName, signatureLabel)
            );

        if (matchedFromEmail) {
            applyFromEmailSelection(matchedFromEmail);
        }
    };


    // Template/signature controls stay independent so users can swap either one
    // without losing the rest of the compose draft.
    const handleSignatureChange = async (e) => {
        try {
            const templateId = e.value?.id;
            if (!templateId) return;

            const response = await axios.get(`/api/v1/email-signatures/${templateId}/`);
            if (response?.data) {
                const { signature_text } = response.data;
                setSelectedSignature(e.value);
                setSignatureText(signature_text || "");
                applyFromEmailForSignature(e.value, response.data);
                
                 if (jobsallpopup || jobssearchedpopup || jobsselectedpopup) {
                setJobDescriptions(prev => {
                    // Remove old signature (if present) and append the new one
                    // You may want to use a more robust way to remove the old signature if needed
                    let bodyWithoutSignature = prev;
                    // Try to remove the previous signatureText or defaultSignature from the end
                    if (signatureText && prev.endsWith(signatureText)) {
                        bodyWithoutSignature = prev.slice(0, -signatureText.length);
                    } else if (defaultSignature && prev.endsWith(defaultSignature)) {
                        bodyWithoutSignature = prev.slice(0, -defaultSignature.length);
                    }
                    return `${bodyWithoutSignature}${signature_text || ""}`;
                });
            }// <-- store signature separately
            }
        } catch (error) {
            console.error("Error fetching signature:", error);
            setSignatureText("");
        }
    };

    const handleTemplateChange = async (e) => {
        try {
            const templateId = e.value?.id;
            if (!templateId) return;

            const response = await axios.get(`/api/v1/email-templates/${templateId}/`);
            if (response?.data) {
                const { body } = response.data;
                setSelectedTemplate(e.value || "");
                setTemplateBody(removeEditorBackgrounds(body || "No Body Template is There!"));  // <-- store template separately
            }
        } catch (error) {
            console.error("Error fetching template:", error);
            setTemplateBody(`
            <p>Dear {{candidate_name}},</p>
            
            <br/>
            ${defaultSignature}
            `);
        }
    };
   
    // const gettemplateitems = async () => {

    //     try {
    //         const transformedData = await loadCachedLookup("templates", async () => {
    //             const response = await axios.get(`/api/v1/email-templates/`, {});
    //             return response.data.map((city) => ({
    //                 label: city.name || "",
    //                 id: city.template_id || "",
    //                 body: city.body || "",
    //             }));
    //         });
    //         setTemplateOptions(transformedData)
    //     } catch (error) {
    //         setTemplateOptions([])
    //     }
    // };

    const getsignatureitems = async () => {
        if (!selectedCompanyId) {
            setSignatureOptions([]);
            setSelectedSignature(null);
            setSignatureText("");
            setDefaultSignature("");
            return;
        }

        setSignatureOptions([]);
        setSelectedSignature(null);
        setSignatureText("");
        setDefaultSignature("");
        setSignatureLoading(true);
        setSignatureError("");
        try {
            const cacheKey = String(selectedCompanyId);
            let signatureRequest = emailSignatureRequestCache.get(cacheKey);
            if (!signatureRequest) {
                signatureRequest = axios
                    .get("/api/v1/email-signatures/", {
                        params: { company_id: selectedCompanyId },
                    })
                    .then(response =>
                        Array.isArray(response.data)
                            ? response.data
                            : response.data?.results || []
                    );
                emailSignatureRequestCache.set(cacheKey, signatureRequest);
            }

            const signatures = await signatureRequest;
            let transformedData = signatures.map((city) => ({
                    ...city,
                    label: city.is_default ? "VGTS" : (city.name || ""),
                    id: city.signature_id || city.id || "",
                    is_default: city.is_default || false,
                    signature_text: city.signature_text || "",
                }));

            // Sort the signatures so that the default one appears first
            transformedData.sort((a, b) => b.is_default - a.is_default);

            const defaultSig =
                transformedData.find((signature) => signature.is_default) ||
                transformedData[0] ||
                null;
            if (defaultSig && !defaultSig.signature_text?.trim()) {
                const details = await axios.get(`/api/v1/email-signatures/${defaultSig.id}/`);
                defaultSig.signature_text = details.data?.signature_text || "";
            }
            setSignatureOptions(transformedData);
            setSelectedSignature(defaultSig);
            setSignatureText(defaultSig?.signature_text || "");
            setDefaultSignature(defaultSig?.signature_text || "");
            setIsDefaultName(Boolean(defaultSig?.is_default));
        } catch (error) {
            console.error("Error fetching signatures:", error);
            emailSignatureRequestCache.delete(String(selectedCompanyId));
            setSignatureOptions([]);
            setSignatureError("Unable to load email signatures.");
        } finally {
            setSignatureLoading(false);
        }
    };

    // Shared dropdown renderers keep the compose toolbar visually consistent.
    const selectedSmsTemplate = (option, props) => {
        if (option) {
            return (
                <div className="flex align-items-center">
                    <i className={`${option.icon} mr-2`}></i>
                    <div>{option.label}</div>
                </div>
            )
        }
        return (
            <div className="flex align-items-center">
                <i className="pi pi-comment mr-2"></i>
                <span>{props.placeholder}</span>
            </div>
        )
    }

    const smsOptionTemplate = option => {
        return (
            <div className="flex align-items-center">
                <i className={`${option.icon} mr-2`}></i>
                <div>{option.label}</div>
            </div>
        )
    }

    const [smsOptions1, setsmsOptions1] = useState([])


    const [uploadedFiles, setUploadedFiles] = useState([]);

    const onUploadHandler1 = async (e) => {
        const newError = {};

        const maxSize = 10 * 1024 * 1024; // 10MB

        const programmingFileExtensions = [
            ".js", ".ts", ".tsx", ".jsx",
            ".java", ".py", ".cpp", ".c", ".cs",
            ".rb", ".php", ".go", ".rs", ".swift",
            ".kt", ".sh", ".pl", ".html", ".xml"
        ];

        const newFiles = e.files || [];

        const validFiles = await Promise.all(
            newFiles.map(async (file) => {
                const fileName = file.name.toLowerCase();

                // ❌ Block programming files by extension
                if (programmingFileExtensions.some(ext => fileName.endsWith(ext))) {
                    newError.attachment = `Programming files are not allowed: ${file.name}`
                    setFormErrors(prev => ({ ...prev, ...newError }));
                    return null;
                }

                // ❌ Block large files
                if (file.size > maxSize) {
                    newError.attachment = `File "${file.name}" exceeds 10MB size limit.`
                    setFormErrors(prev => ({ ...prev, ...newError }));
                    return null;
                }

                const base64 = await getBase64(file);
                return {
                    file_name: file.name,
                    file_data: base64,
                };
            })
        );

        const filteredFiles = validFiles.filter(Boolean);

        if (filteredFiles.length === 0) return;

        setAttachmentfile((prevFiles) => [...prevFiles, ...filteredFiles]);
        setFormErrors(prev => ({ ...prev, attachment: newError.attachment || "" }));

        // ✅ Clear upload component if needed
        if (fileUploadRef.current) {
            fileUploadRef.current.clear(); // ✅ This resets the input value, allowing re-uploads
        }
    };

    const filteredJobs = jobs.filter(job => {
  const search = jobsSearchText.toLowerCase().trim();
  return (
    (job.job_title && job.job_title.toLowerCase().includes(search)) ||
    (job.job_number && job.job_number.toString().toLowerCase().includes(search)) ||
    (job.hiring_manager_name && job.hiring_manager_name.toLowerCase().includes(search))
  );
});


    // const removeFile = (fileName) => {
    //     setUploadedFiles(uploadedFiles.filter((file) => file.name !== fileName));
    // };

    const [frominputemail, setFrominputemail] = useState(null)


    const [frominputemail1, setFrominputemail1] = useState("")


    const [frominputemail2, setFrominputemail2] = useState(null)


    // new email form

    const Fromemailcont = item => {
        return (
            <div>
                <span>{item}</span>
            </div>
        )
    }

    const [tonewemail, setTonewemail] = useState([])
    const [toemailSelected, setToemailSelected] = useState([])
    const [ccemailSelected, setCcemailSelected] = useState([])
    const [toemailSearched, setToemailSearched] = useState([])
    const [ccemailSearched, setCcemailSearched] = useState([])

    const [ccemail, setCcemail] = useState([])
    const getDefaultEmailSubject = () => jobData?.job_title || jobData?.title || "";
    const [subject, setSubject] = useState(getDefaultEmailSubject());
    const Ccemailcont = item => {
        return (
            <div>
                <span>{item}</span>
            </div>
        )
    }

    const [attachmentfile, setAttachmentfile] = useState([])
    const fileListRef = useRef(null)

    const getJdDocumentUrl = () => {
        const documentValue =
            jobData?.jd_document ||
            jobData?.jdDocument ||
            jobData?.job_description_document ||
            jobData?.jobDescriptionDocument ||
            jobData?.description_document ||
            jobData?.document_url ||
            jobData?.documentUrl ||
            jobData?.jd_document_url ||
            jobData?.jdDocumentUrl ||
            jobData?.jd_file ||
            jobData?.jdFile ||
            jobData?.job_description_file ||
            jobData?.jobDescriptionFile ||
            jobData?.job_details?.jd_document ||
            jobData?.job_details?.job_description_document ||
            jobData?.job_details?.jd_document_url;

        if (!documentValue) return "";
        if (typeof documentValue === "string") return documentValue;
        return documentValue?.url || documentValue?.file || documentValue?.document || documentValue?.file_url || documentValue?.document_url || "";
    };

    const getFileNameFromUrl = url => {
        if (!url) return "JD Document";
        const cleanUrl = String(url).split("?")[0];
        const fileName = decodeURIComponent(cleanUrl.split("/").pop() || "").trim();
        return fileName || "JD Document";
    };

    const addJdDocumentAttachment = () => {
        const jdDocument = getJdDocumentUrl();

        setAttachmentfile(prevFiles =>
            prevFiles.filter(file => file.auto_attached_type !== "jd_document")
        );

        if (!jdDocument) return;

        const autoAttachment = {
            file_name: getFileNameFromUrl(jdDocument),
            file_data: jdDocument,
            source_url: jdDocument,
            auto_attached_type: "jd_document",
        };

        setAttachmentfile(prevFiles => {
            const withoutExistingJd = prevFiles.filter(
                file => file.auto_attached_type !== "jd_document"
            );
            const alreadyExists = withoutExistingJd.some(
                file => file.file_name === autoAttachment.file_name
            );
            return alreadyExists
                ? withoutExistingJd
                : [autoAttachment, ...withoutExistingJd];
        });
    };

    useEffect(() => {
        addJdDocumentAttachment();
    }, [
        jobData?.jd_document,
        jobData?.jdDocument,
        jobData?.job_description_document,
        jobData?.jobDescriptionDocument,
        jobData?.description_document,
        jobData?.document_url,
        jobData?.documentUrl,
        jobData?.jd_document_url,
        jobData?.jdDocumentUrl,
        jobData?.jd_file,
        jobData?.jdFile,
        jobData?.job_description_file,
        jobData?.jobDescriptionFile,
        jobData?.job_details?.jd_document,
        jobData?.job_details?.job_description_document,
        jobData?.job_details?.jd_document_url,
    ]);

    const getJdDisplayFileName = () => {
        const jdDocument = getJdDocumentUrl();
        if (jdDocument) return getFileNameFromUrl(jdDocument);
        const title = jobData?.job_title || jobData?.title || "Job Description";
        const normalizedTitle = String(title)
            .trim()
            .replace(/[^a-z0-9]+/gi, "_")
            .replace(/^_+|_+$/g, "");
        return `${normalizedTitle || "Job_Description"}-JD.docx`;
    };

    const shouldShowJdAttachmentChip = Boolean(
        jobData &&
        !hideJdAttachmentChip &&
        (getJdDocumentUrl() || getJobDescriptionContent(jobData) || jobData?.job_title || jobData?.title)
    );

    const attachmentFilesForDisplay =
        attachmentfile.length > 0
            ? attachmentfile
            : shouldShowJdAttachmentChip
            ? [{
                file_name: getJdDisplayFileName(),
                file_data: getJdDocumentUrl(),
                auto_attached_type: "jd_document_display",
                display_only: true,
            }]
            : [];


    // jobs all strats

    const [jobDetailsVisible, setJobDetailsVisible] = useState(false)
    const [selectedJobTitles, setSelectedJobTitles] = useState("")
    const [searchText, setSearchText] = useState("")
    const [selectedJobs, setSelectedJobs] = useState([])
    const [jobDescriptions, setJobDescriptions] = useState("")
    const [jobLinks, setJobLinks] = useState([]);


        const generateLinksForJobs = async (jobNumbers = []) => {
                // Calls the external API to generate signed links for selected job numbers
                try {
                        const token = sessionStorage.getItem('token');
                        const userInfo = JSON.parse(sessionStorage.getItem("atsuser-info"));
                        const companyId = userInfo?.user_details?.company_id;
                        const url = `/api/v1/apply-job/generate-links/`;
                        // API expects job_numbers as array and company_id in POST body
                        const response = await axios.post(url, { job_numbers: jobNumbers, company_id: companyId }, {
                                headers: {
                                        Authorization: token ? `Bearer ${token}` : undefined,
                                        'Content-Type': 'application/json'
                                }
                        });

                        if (response?.data?.links) {
                                setJobLinks(response.data.links);
                                return response.data.links;
                        }
                        setJobLinks([]);
                        return [];
                } catch (error) {
                        console.error('Failed to fetch generate-links:', error);
                        setJobLinks([]);
                        return [];
                }
        };

        const handleSave = async () => {
                // Add default text and format job descriptions
                // let greeting = "Dear {{candidate_name}}";
                if (jobsallpopup || jobssearchedpopup) {
                        greeting = "Dear {{candidate_name}}";
                } else if (selectedJobs && selectedJobs.length > 1) {
                        greeting = "Dear {{candidate_name}}";
                } else if (selectedJobs && selectedJobs.length === 1) {
                        greeting = "Dear {{candidate_name}}";
                }

                const defaultText = `${greeting}, <br><br>
                We have job openings matching your skills. Please find the jobs listed below:`

                // If jobs are selected, call the generate-links API and use its returned job details
                let linksArray = [];
                try {
                        const jobNumbers = selectedJobs.map(j => j.job_number).filter(Boolean);
                        if (jobNumbers.length > 0) {
                                linksArray = await generateLinksForJobs(jobNumbers);
                        }
                } catch (err) {
                        console.error('Error generating links for jobs', err);
                        linksArray = [];
                }

                // Fallback: if API didn't return links, map selectedJobs into a minimal structure
                if (!linksArray || linksArray.length === 0) {
                        linksArray = selectedJobs.map(job => ({
                                job_number: job.job_number,
                                signed_link: `${base_url}apply-job/get-job/${job.job_number}/`,
                                expires_in_hours: null,
                                job_details: job
                        }));
                }

                // Create a vertical table structure for each job's details based on API job_details
                const formattedJobDetails = linksArray
                        .map(link => {
                                const job = link.job_details || {};
                                const jobNum = job.job_number || link.job_number || '';
                                const title = job.job_title || '';
                                const location = job.location_name || job.location || 'N/A';
                                const jobType = job.job_type_name || '';
                                const primarySkills = job.required_skill_names || job.required_skills || 'N/A';
                                const experience = job.required_experience || 0;
                                const description = getJobDescriptionContent(job) || 'N/A';
                                const signedLink = link.signed_link || `${base_url}apply-job/get-job/${jobNum}`;

                                return `
                     <table border="1" cellpadding="5" cellspacing="0" style="width:100%;border-collapse:collapse;border:1px solid #333;font-family:Arial,sans-serif;font-size:14px;">
                         <tr>
                             <td style="background:#f5f5f5;border:1px solid #333;padding:8px;text-align:left; width:25%;"><b> Job Title </b></td>
                             <td style="border:1px solid #333;padding:8px; width:75%;">${title} (${jobNum})</td>
                         </tr>
                         <tr>
                             <td style="background:#f5f5f5;border:1px solid #333;padding:8px;text-align:left; width:25%;"><b> Location </b></td>
                             <td style="border:1px solid #333;padding:8px; width:75%;">${location}</td>
                         </tr>
                         <tr>
                             <td style="background:#f5f5f5;border:1px solid #333;padding:8px;text-align:left; width:25%;"><b> Job Type </b></td>
                             <td style="border:1px solid #333;padding:8px; width:75%;">${jobType}</td>
                         </tr>
                         <tr>
                             <td style="background:#f5f5f5;border:1px solid #333;padding:8px;text-align:left; width:25%;"><b> Primary Skills </b></td>
                             <td style="border:1px solid #333;padding:8px; width:75%;">${primarySkills}</td>
                         </tr>
                         <tr>
                             <td style="background:#f5f5f5;border:1px solid #333;padding:8px;text-align:left; width:25%;"><b> Work Experience </b></td>
                             <td style="border:1px solid #333;padding:8px; width:75%;">${experience} Years</td>
                         </tr>
                         <tr>
                             <td style="background:#f5f5f5;border:1px solid #333;padding:8px;text-align:left; width:25%;"><b> Job Description </b> </td>
                             <td style="border:1px solid #333;padding:8px; width:75%; word-wrap: break-word; white-space: normal;">${description}</td>
                         </tr>
                             <tr>
                             <td style="background:#f5f5f5;border:1px solid #333;padding:8px;text-align:left; width:25%;"><b> Apply Job </b> </td>
                             <td style="border:1px solid #333;padding:8px; width:75%;"><a href="${signedLink}" title="${signedLink}">Click here to submit resume!</a></td>
                         </tr>
                     </table><br><br>
                 `
                        })
                        .join("")

                const signatureToUse = defaultSignature && defaultSignature.trim()
                        ? defaultSignature
                        : '';

                const finalText = `${defaultText}<br/><br/>${formattedJobDetails}${signatureToUse}`
                const selectedTitles = selectedJobs.map(job => job.title || job.job_title).filter(Boolean).join(", ");
                setJobDescriptions(finalText) // Set the combined text in the editor
                setSelectedJobTitles(selectedTitles)
                setSubject(selectedTitles || getDefaultEmailSubject())
                setJobDetailsVisible(false) // Close the Job Details dialog
                setSelectedJobs([])
        }

    const handleRefresh = () => {
        setSelectedJobs([])
        setSearchText("")
    }

    // popup
    const [jobsallpopup, SetJobsallPopup] = useState(false)
    const [jobssearchedpopup, SetJobssearchedPopup] = useState(false)
    const [jobsselectedpopup, SetJobsselectedPopup] = useState(false)

    //sais code
    const resetSignatureAndTemplate = () => {
    // Find default signature if available
    const defaultSig = signatureOptions.find(sig => sig.is_default) || signatureOptions[0];
    setSelectedSignature(defaultSig || null);
    setSignatureText(defaultSig ? defaultSig.signature_text : "");
    setSelectedTemplate(null);
    setTemplateBody("");
};


    const handleOpenDialogs = () => {
        if (guardSmtp()) return;
        // Open the first dialog
        SetJobsallPopup(true)
        setPrimaryFromEmailDefault();

        // Automatically open the second dialog after a short delay
        setTimeout(() => {
            setJobDetailsVisible(true)
        }, 300) // Adjust delay as needed (300ms in this example)
    }

    const handleSearchedDialogs = () => {
        if (guardSmtp()) return;
        // Open the first dialog
        SetJobssearchedPopup(true)

        // Automatically open the second dialog after a short delay
        setTimeout(() => {
            setJobDetailsVisible(true)
        }, 300) // Adjust delay as needed (300ms in this example)
    }

    const handleSelectedDialogs = () => {
        // Open the first dialog
        SetJobsselectedPopup(true)

        // Automatically open the second dialog after a short delay
        setTimeout(() => {
            setJobDetailsVisible(true)
        }, 3000) // Adjust delay as needed (300ms in this example)
    }
    const setPrimaryFromEmailDefault = () => {
    if (Array.isArray(fromEmail) && fromEmail.length > 0) {
        const primary = fromEmail.find(f => f.is_primary);
        if (primary) {
            setFrominputemail(primary);
            setFromemailjobsSelectedTo([primary.code]);
            setFromemailjobs([primary.code]);
        }
    }
};

    const resolveFromEmailOption = value =>
        (Array.isArray(fromEmail) ? fromEmail.find(opt => opt.code === value) : null) || null

    const applyFromEmailSelection = option => {
        const selectedCode = option?.code || option?.from_email || ""
        setFrominputemail(option || null)
        setFromemailjobsSelectedTo(selectedCode ? [selectedCode] : [])
        setFromemailjobs(selectedCode ? [selectedCode] : [])
    }
    

    // jobs all ends

    // jobs all chip starts
    const [toemailjobs, setToemailjobs] = useState([
        "All Candidates"
        // "Michael Chris (test2@gmail.com)",
    ])
    const [ccemailjobsall, setCcemailjobsall] = useState([])
    const [toemailseljobs, setToemailseljobs] = useState([
    ])
    const [toemailsearchjobs, setToemailsearchjobs] = useState([
    ])
    const [ccemailjobssear, setCcemailjobssear] = useState([])
    const [ccemailjobsSearched, setCcemailjobsSearched] = useState([])


    const [fromemailjobsSelectedTo, setFromemailjobsSelectedTo] = useState([])
    const [fromemailAll, setFromemailAll] = useState(["All Candidates (allcandidates@gmail.com)"])
    const [ccemailall, setCcemailall] = useState([])

    const [fromemailjobs, setFromemailjobs] = useState([])
    const Fromemailcontjobs = item => {
        return (
            <div>
                <span>{item}</span>
            </div>
        )
    }

    const Ccemailcontjobs = item => {
        return (
            <div>
                <span>{item}</span>
            </div>
        )
    }
    // jobs all chip ends

    // email dropdown

    const [selectedActEmail, setSelectedActEmail] = useState(null)
    const openSelectedEmailComposer = async (savedCandidates) => {
    let reviewedCandidates = Array.isArray(savedCandidates) ? savedCandidates : selectedData || [];
    if (onBeforeOpen && !Array.isArray(savedCandidates)) {
        const canOpen = await onBeforeOpen();
        if (canOpen === false) return;
        if (Array.isArray(canOpen)) reviewedCandidates = canOpen;
    }
    // Always re-fetch and validate live before opening
    try {
        const fromEmailCacheKey = loggedInUserId
            ? `fromEmails:${loggedInUserId}`
            : "fromEmails";

        const transformedData = await loadCachedLookup(fromEmailCacheKey, async () => {
            const response = await axios.get(
                loggedInUserId
                    ? `/api/v1/smtp-accounts/?user_id=${encodeURIComponent(loggedInUserId)}&page=1&size=50`
                    : `/api/v1/smtp-accounts/`,
            );
            const smtpRows = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];
            return smtpRows.map((item) => ({
                ...item,
                name: item.from_email || "",
                code: item.from_email,
                is_primary: item.is_primary,
                company_id: item.company_id ?? item.company?.company_id ?? item.company?.id ?? item.company,
                company_name: item.company_name ?? item.company?.company_name ?? item.company?.name ?? "",
            }));
        });

        const loggedInEmail = userEmail?.toLowerCase?.() || "";
        const hasSmtp = loggedInEmail
            ? transformedData.some(f => f.code?.toLowerCase() === loggedInEmail)
            : transformedData.length > 0;

        if (!hasSmtp) {
            Swal.fire({
                icon: "warning",
                title: "SMTP Not Configured",
                html: `Your email account (<b>${userEmail || "logged-in user"}</b>) is not found in the SMTP list.<br/><br/>Please configure your SMTP account before sending emails.`,
                confirmButtonText: "OK",
            });
            return;
        }
    } catch (err) {
        console.error("SMTP check failed", err);
        // If we can't verify, allow through — don't block on network error
    }

    setPrimaryFromEmailDefault();
    setIncludeJD(false);
    setTemplateBody("");
    setSelectedTemplate(null);
    emailSignatureRequestCache.delete(String(selectedCompanyId));
    await getsignatureitems();
    setHideJdAttachmentChip(false);
    addJdDocumentAttachment();
    const emails = reviewedCandidates
        .map(candidate => candidate?.email || candidate?.candidate_email || candidate?.to_email || "")
        .filter(email => email);
    setToemailSelected(emails);
    if (emails.length > 0) {
        SetEmailviewpopSelected(true);
    } else {
        Swal.fire({
            icon: "warning",
            title: "No Candidates Selected",
            text: "Please select at least one candidate with an email address.",
            confirmButtonText: "OK",
        });
    }
};

    const handledOpenRequest = useRef(null);
    useEffect(() => {
        if (!openRequest || handledOpenRequest.current === openRequest.requestId) return;
        handledOpenRequest.current = openRequest.requestId;
        openSelectedEmailComposer(openRequest.candidates);
        onOpenRequestHandled?.();
    }, [openRequest]);

    const actEmailOptions = [
        {
            name: "New Email",
            code: "EM-NE",
            action: () => {
                if (guardSmtp()) return;
        setPrimaryFromEmailDefault();
        SetEmailviewpop(true);
    },
        },
        {
            name: "Selected",
            code: "EM-SE",
            action: openSelectedEmailComposer,
        },
        {
            name: "Searched",
            code: "EM-SE",
            action: () => {
            setPrimaryFromEmailDefault(); // <-- Add this line
            SetEmailviewpopSearched(true);
        },

        },
        {
            name: "All",
            code: "EM-AL",
            action: () => {
            setPrimaryFromEmailDefault(); // <-- Add this line
            SetEmailviewpopAll(true);
        },
        },
        {
            name: "Jobs",
            code: "EM-JO",
            subItems: [
                { name: "All", code: "EM-JO-AL",action: () => {
                    setPrimaryFromEmailDefault(); // <-- Add this line
                    //  sais code
                    handleOpenDialogs();
                } }, // "All" under Jobs
                { name: "Searched", code: "EM-JO-SE",action: () => {
                    setPrimaryFromEmailDefault();
                    //  sais code
                    handleSearchedDialogs();
                } },
                {
                    name: "Selected",
                    code: "EM-JO-SM",
                     action: () => {
                        if (guardSmtp()) return;
                    setPrimaryFromEmailDefault(); // <-- Add this line
                    // Populate toemailseljobs with candidate_email from selectedData for Jobs > Selected
                    const emails = selectedData.map(candidate => candidate.email).filter(email => email);
                    setToemailseljobs(emails);
                    setToemailSelected(emails);
                    if (emails.length > 0) {
                        setToemailseljobs(emails);
                        SetJobsselectedPopup(true);
                        setTimeout(() => {
                            setJobDetailsVisible(true);
                        }, 300);
                    } else {
                        SetJobsselectedPopup(true); // Show fallback dialog
                    }
                }
                },
            ],
        },
    ]

    const handleEmailChange = e => {
        setSelectedActEmail(e.value)
         // sais code
        if (e.value?.action) {
        e.value.action(); // Always call the action, which now sets the primary email
    }
    // sais code
        // Check for specific subItems like "All"
        // if (e.value?.code === "EM-JO-AL") {
        //     // console.log("All clicked!") // Debug log
        //     handleOpenDialogs(true) // Open dialog
        // } else if (e.value?.code === "EM-JO-SM") {
        //     // Populate toemailseljobs with email from selectedData for Jobs > Selected
        //     const emails = selectedData.map(candidate => candidate.email).filter(email => email);
        //     setToemailseljobs(emails);
        //     setToemailSelected(emails);
        //     if (emails.length > 0) {
        //         setToemailseljobs(emails);
        //         SetJobsselectedPopup(true);
        //         setTimeout(() => {
        //             setJobDetailsVisible(true);
        //         }, 300);
        //     } else {
        //         SetJobsselectedPopup(true); // Show fallback dialog
        //     }
        //     // handleSelectedDialogs(true) // Handle top-level actions if defined
        // }
        // else if (e.value?.code === "EM-JO-SE") {

        //     handleSearchedDialogs(true);

        // }
        // else if (e.value?.action) {
        //     e.value.action() // Handle top-level actions if defined
        // }
    }

    // email banner starts
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedSmsOption, setSelectedSmsOption] = useState(null)

    const [templateOptions, setTemplateOptions] = useState([]);
    const [signatureOptions, setSignatureOptions] = useState([]);



    const [fromEmail, setfromEmail] = useState([])
    const fetchFromMails = async () => {
        try {
            const fromEmailCacheKey = loggedInUserId
                ? `fromEmails:${loggedInUserId}`
                : "fromEmails";

        const transformedData = await loadCachedLookup(fromEmailCacheKey, async () => {
            const response = await axios.get(
                loggedInUserId
                    ? `/api/v1/smtp-accounts/?user_id=${encodeURIComponent(loggedInUserId)}&page=1&size=50`
                    : `/api/v1/smtp-accounts/`,
            );
            const smtpRows = Array.isArray(response.data)
                ? response.data
                : response.data?.results || [];
            return smtpRows.map((item) => ({
                ...item,
                name: item.from_email || "",
                code: item.from_email,
                is_primary: item.is_primary,
                company_id: item.company_id ?? item.company?.company_id ?? item.company?.id ?? item.company,
                company_name: item.company_name ?? item.company?.company_name ?? item.company?.name ?? "",
            }));
        });
        setfromEmail(transformedData);
           const loggedInEmail = userEmail?.toLowerCase?.() || "";
            const hasSmtp = loggedInEmail
                ? transformedData.some(f => f.code?.toLowerCase() === loggedInEmail)
                : transformedData.length > 0;

            if (!hasSmtp) {
                setSmtpMissing(true);
            } else {
                setSmtpMissing(false);
            }
            // ── END NEW ──

        const primary =
            transformedData.find(f => f.is_primary) || transformedData[0];
        if (primary) {
            setFromemailjobsSelectedTo([primary.code]);
            setFromemailjobs([primary.code]);
            setFrominputemail(primary);
        }

    } catch (error) {
        console.error('Error fetching emails:', error);
    }
};

    const [selectedSignature, setSelectedSignature] = useState(null);

    useEffect(() => {
        if (selectedSignature && Array.isArray(fromEmail) && fromEmail.length > 0) {
            applyFromEmailForSignature(selectedSignature);
        }
    }, [selectedSignature, fromEmail]);

    useEffect(() => {
        if (jobsselectedpopup || jobsallpopup || jobssearchedpopup || emailviewpopAll || emailviewpopSelected) {
            fetchFromMails();
        }
    }, [jobsselectedpopup, jobsallpopup, jobssearchedpopup, emailviewpopAll, emailviewpopSelected, loggedInUserId]);


    // email banner ends


    // candidates to popup starts

    const [linkCantoVisible, setLinkCantoVisible] = useState(false);
    const [selectedCanto, setSelectedCanto] = useState([]);



    // const filteredCanto = canto?.filter((candidate) => {
    //     const fullName = `${candidate.first_name || ""} ${candidate.last_name || ""}`.toLowerCase();

    //     return (
    //         candidate.job_title?.toLowerCase().includes(searchText.toLowerCase()) ||
    //         fullName.includes(searchText.toLowerCase()) || // <-- combined name check
    //         candidate.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    //         candidate.candidate_id?.toString().includes(searchText)
    //     );
    // });

    const filteredCanto = (data) =>
        data.filter((item) => {
            const searchTextLower = searchText.toLowerCase().trim();
            const fullName = `${item.first_name || ''} ${item.last_name || ''}`.toLowerCase();
            const jobTitle = item.job_title ? item.job_title.toLowerCase() : '';
            const email = item.email ? item.email.toLowerCase() : '';
            const candidateId = item.candidate_id ? item.candidate_id.toString() : '';

            return (
                fullName.includes(searchTextLower) ||
                jobTitle.includes(searchTextLower) ||
                email.includes(searchTextLower) ||
                candidateId.includes(searchTextLower)
            );
        });
    // const handleSaveCanto = () => {
    //     const canDetails = selectedCanto.map((candidate) => ({
    //         name: candidate.first_name + " " + candidate.last_name,
    //         email: candidate.email,
    //     }));
    //     const formattedCandidates = canDetails.map(
    //         (contact) => contact.email
    //     );
    //     setTonewemail(prev => [...prev, ...formattedCandidates]);
    //     setToemailSelected(prev => [...prev, ...formattedCandidates]);
    //     setToemailSearched(prev => [...prev, ...formattedCandidates]);
    //     setFromemailAll(prev => [...prev, ...formattedCandidates]);
    //     setToemailjobs(prev => [...prev, ...formattedCandidates]);
    //     setToemailseljobs(prev => [...prev, ...formattedCandidates]);
    //     setToemailsearchjobs(prev => [...prev, ...formattedCandidates]);
    //     setLinkCantoVisible(false);
    // };

    const handleSaveCanto = () => {
        const canDetails = selectedCanto.map((candidate) => ({
            name: `${candidate.first_name} ${candidate.last_name}`,
            email: candidate.email || "N/A",
        }));

        const newEmails = canDetails.map(contact => contact.email).filter(email => email?.trim());

        const mergeUnique = (prev, incoming) => {
            const emailSet = new Set([...prev, ...incoming]);
            return Array.from(emailSet);
        };

        setTonewemail(prev => mergeUnique(prev, newEmails));
        setToemailSelected(prev => mergeUnique(prev, newEmails));
        setToemailSearched(prev => mergeUnique(prev, newEmails));
        setFromemailAll(prev => mergeUnique(prev, newEmails));
        setToemailjobs(prev => mergeUnique(prev, newEmails));
        setToemailseljobs(prev => mergeUnique(prev, newEmails));
        setToemailsearchjobs(prev => mergeUnique(prev, newEmails));

        setLinkCantoVisible(false);
        setSelectedCanto([]); // Clear selected candidates
        setSearchText(""); // Clear search text
    };


    const handleRefresh1to = () => {
        setSelectedCanto([]); // Reset selected contacts
        setSearchText(""); // Reset search text
    };

    // candidates to popup ends


    // candidates cc popup starts

    const [linkCanccVisible, setLinkCanccVisible] = useState(false);
    const [selectedCancc, setSelectedCancc] = useState([]);

    useEffect(() => {
        if (linkCantoVisible || linkCanccVisible) {
            getAllActive(lazyParams.page, lazyParams.rows);
        }
    }, [lazyParams, linkCantoVisible, linkCanccVisible]);

    const filteredCancc = canto?.filter((candidate) => {
        const fullName = `${candidate.first_name || ""} ${candidate.last_name || ""}`.toLowerCase();

        return (
            candidate.job_title?.toLowerCase().includes(searchText.toLowerCase()) ||
            fullName.includes(searchText.toLowerCase()) || // <-- combined name check
            candidate.email?.toLowerCase().includes(searchText.toLowerCase()) ||
            candidate.candidate_id?.toString().includes(searchText)
        );
    });

 const handleEditorPaste = (setter) => () => {
  setTimeout(() => {
    setter(prev => linkifyEditorHtml(prev));
  }, 50);
};
    // const handleSaveCancc = () => {
    //     const canDetails = selectedCancc.map((candidate) => ({
    //         name: candidate.first_name + " " + candidate.last_name,
    //         email: candidate.email,
    //     }));
    //     const formattedCandidates = canDetails.map(
    //         (candidate) => `${candidate.name} - (${candidate.email})`
    //     );
    //     setCcemail(prev => [...prev, ...formattedCandidates]);
    //     setCcemailSelected(prev => [...prev, ...formattedCandidates]);
    //     setCcemailSearched(prev => [...prev, ...formattedCandidates]);
    //     setCcemailall(prev => [...prev, ...formattedCandidates]);
    //     setCcemailjobsall(prev => [...prev, ...formattedCandidates]);
    //     setCcemailjobsSearched(prev => [...prev, ...formattedCandidates]);
    //     setCcemailjobssear(prev => [...prev, ...formattedCandidates]);
    //     setLinkCanccVisible(false);
    // };

    const handleSaveCancc = () => {
        const canDetails = selectedCancc.map((candidate) => ({
            name: `${candidate.first_name} ${candidate.last_name}`,
            email: candidate.email,
        }));

        const formattedCandidates = canDetails.map(
            (candidate) => `${candidate.email}`
        );

        // Helper to remove duplicates from merged array
        const mergeUnique = (prev, newItems) => {
            const set = new Set(prev);
            newItems.forEach(item => set.add(item));
            return Array.from(set);
        };

        setCcemail(prev => mergeUnique(prev, formattedCandidates));
        setCcemailSelected(prev => mergeUnique(prev, formattedCandidates));
        setCcemailSearched(prev => mergeUnique(prev, formattedCandidates));
        setCcemailall(prev => mergeUnique(prev, formattedCandidates));
        setCcemailjobsall(prev => mergeUnique(prev, formattedCandidates));
        setCcemailjobsSearched(prev => mergeUnique(prev, formattedCandidates));
        setCcemailjobssear(prev => mergeUnique(prev, formattedCandidates));

        setLinkCanccVisible(false);
        setSelectedCancc([]); // Clear selected candidates
        setSearchText(""); // Clear search text
    };


    const handleRefresh1cc = () => {
        setSelectedCancc([]); // Reset selected contacts
        setSearchText(""); // Reset search text
    };

    useEffect(() => {
        // gettemplateitems();
        getsignatureitems();
        // fetchFromMails();
    }, [selectedCompanyId]);

    // Set default signature when dialogs open
    useEffect(() => {
        if (emailviewpop && signatureOptions.length > 0) {
            const defaultSig = signatureOptions.find(sig => sig.is_default) || signatureOptions[0];
            if (defaultSig) {
                setSelectedSignature(defaultSig);
                setSignatureText(defaultSig.signature_text);
            }
        }
    }, [emailviewpop, signatureOptions]);

    useEffect(() => {
        if (emailviewpopSelected && signatureOptions.length > 0) {
            const defaultSig = signatureOptions.find(sig => sig.is_default) || signatureOptions[0];
            if (defaultSig) {
                setSelectedSignature(defaultSig);
                setSignatureText(defaultSig.signature_text);
            }
        }
    }, [emailviewpopSelected, signatureOptions]);

    useEffect(() => {
        if (emailviewpopSearched && signatureOptions.length > 0) {
            const defaultSig = signatureOptions.find(sig => sig.is_default) || signatureOptions[0];
            if (defaultSig) {
                setSelectedSignature(defaultSig);
                setSignatureText(defaultSig.signature_text);
            }
        }
    }, [emailviewpopSearched, signatureOptions]);

    useEffect(() => {
        if (emailviewpopAll && signatureOptions.length > 0) {
            const defaultSig = signatureOptions.find(sig => sig.is_default) || signatureOptions[0];
            if (defaultSig) {
                setSelectedSignature(defaultSig);
                setSignatureText(defaultSig.signature_text);
            }
        }
    }, [emailviewpopAll, signatureOptions]);
    const onPageChange = (event) => {
        setLazyParams({ page: event.page, rows: event.rows });
    };

    // const getRecipientsForConfirm = () => {
    //     if ((emailviewpopSelected || jobsselectedpopup) && Array.isArray(selectedData) && selectedData.length) {
    //         return selectedData.map(c => ({
    //             name: [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(" ")
    //                 || c.candidate_name || c.name || "--",
    //             email: c.email || c.candidate_email || "--",
    //         }));
    //     }
    //     let toList = [];
    //     if (emailviewpop) toList = tonewemail;
    //     else if (emailviewpopAll || jobsallpopup) toList = ["All Candidates"];
    //     else if (emailviewpopSearched) toList = toemailSearched;
    //     else if (jobssearchedpopup) toList = toemailsearchjobs;
    //     else toList = toemailSelected;

    //     return (toList || []).map(email => ({ name: "--", email }));
    // };
        const getRecipientsForConfirm = () => {
            let toList = [];
            if (emailviewpop) toList = tonewemail;
            else if (emailviewpopAll || jobsallpopup) toList = ["All Candidates"];
            else if (emailviewpopSearched) toList = toemailSearched;
            else if (jobssearchedpopup) toList = toemailsearchjobs;
            else toList = toemailSelected;

            return (toList || []).map(email => {
                const candidate = Array.isArray(selectedData)
                    ? selectedData.find(
                        c => (c?.email || c?.candidate_email || "").trim().toLowerCase() === (email || "").trim().toLowerCase()
                    )
                    : null;
                const name = candidate
                    ? [candidate.first_name, candidate.middle_name, candidate.last_name].filter(Boolean).join(" ")
                        || candidate.candidate_name || candidate.name || "--"
                    : "--";
                return { name, email };
            });
        };
    const openRecipientPreview = emails => {
        const emailList = Array.isArray(emails) ? emails.filter(Boolean) : [];
        if (!emailList.length) return;

        const rows = emailList.map((email, index) => {
            const candidate = Array.isArray(selectedData)
                ? selectedData.find(item => (item?.email || item?.candidate_email) === email)
                : null;
            const name = candidate
                ? [candidate?.first_name, candidate?.middle_name, candidate?.last_name]
                    .filter(Boolean)
                    .join(" ")
                    .trim() || candidate?.candidate_name || candidate?.name || "--"
                : "--";

            return {
                id: index + 1,
                name,
                email,
            };
        });

        setRecipientPreviewList(rows);
        setRecipientPreviewVisible(true);
    };

    const sendNewMail = async () => {
        const fromAddress = fromemailjobsSelectedTo[0] || ""
        const newErrors = {};

        // Validate From email
        if (!fromemailjobsSelectedTo || fromemailjobsSelectedTo.length === 0 || !fromemailjobsSelectedTo[0]?.trim()) {
            newErrors.from = "From email is required";
        }

        // Validate To email (only for non-All dialogs)
        if (!emailviewpopAll && !jobsallpopup && (!toemailSelected || toemailSelected.length === 0 || toemailSelected.every(email => !email?.trim()))) {
            newErrors.to = "To address is required";
        }

        // Validate Subject
        // if (!subject?.trim()) {
        //     newErrors.subject = "Subject is required";
        // }

        // Validate Email Body
        const bodyToValidate = jobsallpopup || jobssearchedpopup || jobsselectedpopup ? jobDescriptions : emailtextEditor;
        if (!bodyToValidate?.trim() || bodyToValidate === "<p><br></p>") {
            newErrors.body = "Email body is required";
        }

        // If there are validation errors, set them and stop
        if (Object.keys(newErrors).length > 0) {
            setFormErrors(newErrors);
            return;
        }

        try {
            setFormErrors({});
            setIsLoading(true);

            // const payload = {
            //     from_address: fromemailjobsSelectedTo[0], // Single email for from_address
            //     to_addresses: emailviewpopAll || jobsallpopup ? ["All Candidates"] : toemailSelected.filter(email => email?.trim()), // It was All Candidates pipeline pranaya asked to changed to All Candidates on 26/7/2025
            //     // cc_addresses: (jobsallpopup ? ccemailjobsall : ccemailSelected).filter(email => email?.trim()), // Use ccemailjobsall for Jobs All
            //     // AFTER
            //     cc_addresses: (
            //     jobsallpopup    
            //         ? ccemailjobsall
            //         : jobssearchedpopup
            //         ? ccemailjobsSearched
            //         : jobsselectedpopup
            //         ? ccemailjobssear
            //         : emailviewpopAll
            //         ? ccemailall
            //         : emailviewpopSearched
            //         ? ccemailSearched
            //         : ccemailSelected  // emailviewpopSelected and emailviewpop (New Email)
            //     ).filter(email => email?.trim()),
            //     subject: subject,
            //     body: jobsallpopup || jobssearchedpopup || jobsselectedpopup ? jobDescriptions : emailtextEditor,
            //     attachments: attachmentfile || [],
            //     is_private: privateChecked,
            //     job_candidate_ids: selectedJobCandidateIds,
            // };
            const toAddressesForPayload = emailviewpopAll || jobsallpopup
                ? ["All Candidates"]
                : toemailSelected.filter(email => email?.trim());

            const payload = {
                from_address: fromAddress, // Single email for from_address
                to_addresses: toAddressesForPayload,
                cc_addresses: (
                jobsallpopup
                    ? ccemailjobsall
                    : jobssearchedpopup
                    ? ccemailjobsSearched
                    : jobsselectedpopup
                    ? ccemailjobssear
                    : emailviewpopAll
                    ? ccemailall
                    : emailviewpopSearched
                    ? ccemailSearched
                    : ccemailSelected  // emailviewpopSelected and emailviewpop (New Email)
                ).filter(email => email?.trim()),
                subject: subject,
                body: prepareOutgoingEmailHtml(jobsallpopup || jobssearchedpopup || jobsselectedpopup ? jobDescriptions : emailtextEditor),
                attachments: attachmentfile || [],
                is_private: privateChecked,
                job_candidate_ids: emailviewpopAll || jobsallpopup
                    ? []
                    : getJobCandidateIdsForEmails(toAddressesForPayload),
            };

            const response = await axios.post(
                "/api/v1/emails/",
                payload,
                // loggedInUserId ? { params: { user_id: loggedInUserId } } : undefined
            );

            if (isSuccessfulDelivery(response, [200, 201, 202])) {
                const responseData = response?.data || {};
                const taskPromise = waitForEmailTask(responseData.task_id);

                // The queue response is already successful, so close the email form
                // and show the acknowledgement while the delivery task runs.
                SetEmailviewpop(false);
                SetEmailviewpopSelected(false);
                SetEmailviewpopSearched(false);
                SetEmailviewpopAll(false);
                SetJobsallPopup(false);
                SetJobssearchedPopup(false);
                SetJobsselectedPopup(false);
                if (!(emailviewpopAll || jobsallpopup)) {
                    onClose?.(getJobCandidateIdsForEmails(payload.to_addresses));
                }

                const queueAlertPromise = Swal.fire({
                    icon: "success",
                    title: "Email sent",
                    text: responseData.message || "We're on it! Your email is being processed and will be sent shortly.",
                    confirmButtonText: "OK",
                });

                try {
                    const taskResult = await taskPromise;
                    if (taskResult.status === "FAILED") {
                        Swal.close();
                        const failure = getTaskFailureDetails(taskResult.taskData);
                        const failedFrom = failure.fromAddress || fromAddress;
                        const failureReason = failure.message || "SMTP authentication failed. Please verify your SMTP settings.";
                        const recipientText = failure.recipient ? ` Recipient: ${failure.recipient}.` : "";
                        await Swal.fire({
                            icon: "error",
                            title: "Email failed",
                            text: `Email failed for ${failedFrom}.${recipientText} ${failureReason}`,
                            confirmButtonText: "OK",
                        });
                        return;
                    }

                    if (taskResult.status === "TIMEOUT") {
                        Swal.close();
                        await Swal.fire({
                            icon: "warning",
                            title: "Email status unavailable",
                            text: "The email was queued, but the backend did not return a final delivery status.",
                            confirmButtonText: "OK",
                        });
                        return;
                    }
                } catch (pollError) {
                    Swal.close();
                    await Swal.fire({
                        icon: "error",
                        title: "Email failed",
                        text: `Email failed for ${fromAddress}. ${pollError?.response?.data?.error || pollError?.response?.data?.message || pollError?.message || "The email delivery task could not be checked."}`,
                        confirmButtonText: "OK",
                    });
                    return;
                }

                // The initial queue acknowledgement is the only success Swal.
                // Do not show another success alert after task completion.
                await queueAlertPromise;

                // Reset all form fields
                setFrominputemail(null);
                setFromemailjobsSelectedTo([]);
                setFromemailjobs([]);
                setTonewemail([]);
                setToemailSelected([]);
                setToemailSearched([]);
                setFromemailAll([]);
                setToemailjobs([]);
                setToemailseljobs([]);
                setToemailsearchjobs([]);
                setCcemail([]);
                setCcemailSelected([]);
                setCcemailSearched([]);
                setCcemailall([]);
                setCcemailjobsall([]);
                setCcemailjobsSearched([]);
                setCcemailjobssear([]);
                setSubject(getDefaultEmailSubject());
                setEmailtextEditor(`
  <p>Dear {{candidate_name}},</p>
    <br />
  ${defaultSignature}
                `);
                setJobDescriptions("");
                setAttachmentfile([]);
                setSelectedTemplate(null);
                setSelectedSignature(null);
                setPrivateChecked(false);
                setFormErrors({});
                setSelectedCanto([]);
                setSelectedCancc([]);
                if (fileUploadRef.current) {
                    fileUploadRef.current.clear();
                }
            } else {
                await Swal.fire({
                    icon: "error",
                    title: "Email failed",
                    text: deliveryErrorMessage(response.data, fromAddress),
                    confirmButtonText: "OK",
                });
            }
        } catch (err) {
            console.error(err);
            await Swal.fire({
                icon: "error",
                title: "Email failed",
                text: deliveryErrorMessage(err?.response?.data, fromAddress),
                confirmButtonText: "OK",
            });
        } finally {
            setIsLoading(false);
        }
    };


    const selectedEmailTemplate1 = (option, props) => {
        if (option) {
            return (
                <div className="flex align-items-center">
                    <i className={`${option.icon} mr-2`}></i>
                    <div>{option.label}</div>
                </div>
            )
        }
        return (
            <div className="flex align-items-center">
                <i className="pi pi-envelope mr-2"></i>
                <span>{props.placeholder}</span>
            </div>
        )
    }
    const emailOptionTemplate1 = option => {
        return (
            <div className="flex align-items-center">
                <i className={`${option.icon} mr-2`}></i>
                <div>{option.label}</div>
            </div>
        )
    }

    // sais code    

    const [previewFile, setPreviewFile] = useState(null);
    const removeFile = (fileName) => {
        setAttachmentfile(attachmentfile.filter((file) => file.file_name !== fileName));
    };

const toCamelCaseName = (name) => {
  if (!name) return "";
  return name
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
    useEffect(() => {
        if (signatureOptions.length > 0) {
            const defaultSignature = signatureOptions.find(signature => signature.is_default);
            if (defaultSignature) {
                setSelectedSignature(defaultSignature);
            }
        }
    }, [signatureOptions]);

     useEffect(() => {
        const candidateGreetingName = getCandidateGreetingName();
        let greeting = `Dear <strong>${escapeEmailHtml(candidateGreetingName)}</strong>,`;
    if (emailviewpopAll || jobsallpopup || jobssearchedpopup) {
        greeting = "Dear <strong>{{candidate_name}}</strong>,";
    } else if (selectedCandidateCount > 1) {
        greeting = "Dear <strong>{{candidate_name}}</strong>,";
    } else if (selectedCandidateCount === 1) {
        greeting = `Dear <strong>${escapeEmailHtml(candidateGreetingName)}</strong>,`;
    }
    const loggedInName = getLoggedInUserName();


let signatureToUse = "";
if (selectedSignature) {
  const apiSignatureHtml = normalizeEmailSignatureHtml(signatureText);
  signatureToUse = `
    <b style="color:#ED7D31; margin:0;">Thanks and Regards,</b> <br />
    ${loggedInName 
  ? `<p style="margin:0;">
        <strong style="color:#1F4E79;">
          ${toCamelCaseName(loggedInName)}
        </strong>
    </p>`
  : ""}
    <p style="margin:0;">
      <b style="color:#1F4E79;">Team Talent Acquisition</b>
    </p>
    ${apiSignatureHtml}
  `;
} else if (defaultSignature) {
  signatureToUse = `
    <p>Thanks and Regards,</p>
    ${loggedInName ? `<p><strong>${loggedInName}</strong></p>` : ""}
    ${normalizeEmailSignatureHtml(defaultSignature)}
  `;
} else {
  signatureToUse = `
    <p>Thanks and Regards,</p>
    ${loggedInName ? `<p><strong>${loggedInName}</strong></p>` : ""}
  `;
}

    // Build job info HTML for email
    let jobInfoHtml = "";
    if (jobData && includeJD) {
        console.info("Job data for email:", jobData); // Debug log to check jobData structure
      const jobTitle = jobData?.job_title || "Job Opportunity";
      const companyName = jobData?.company_name || "";
      const companyDesc = jobData?.company_description || "";
      const location = Array.isArray(jobData?.location) 
        ? jobData.location.join(", ") 
        : (jobData?.location || "");
      const jobType = jobData?.job_type || jobData?.employment_type || "";
      const salary = jobData?.salary || jobData?.salary_range || "";
      const shift = jobData?.shift || "";
      console.log("myshift",shift)
      const skills = Array.isArray(jobData?.skills) 
        ? jobData.skills.join(", ") 
        : (jobData?.skills || "");
      const experience = `${jobData?.min_exp || ""} - ${jobData?.max_exp || ""} years`.replace(/^[\s\-]*|[\s\-]*$/g, "");
      const rolesAndResponsibilities = getJobDescriptionContent(jobData);
      const formattedRoles = formatJobDescriptionForEmail(rolesAndResponsibilities);
      

      jobInfoHtml = `
          <table role="presentation" cellpadding="0" cellspacing="0" border="1" width="100%" style="width: 100%; border-collapse: collapse; border: 1px solid #9fb7cf; mso-table-lspace: 0pt; mso-table-rspace: 0pt; font-family: Arial, sans-serif; font-size: 14px; line-height: 20px; margin: 8px 0 14px;">
            <tbody>
              ${buildEmailInfoRow("Company Name", companyName)}
              ${buildEmailInfoRow("Company Description", companyDesc)}
              ${buildEmailInfoRow("Job Title", jobTitle)}
              ${buildEmailInfoRow("Skills Required", skills)}
              ${buildEmailInfoRow("Job Type", jobType)}
              ${experience && experience !== "years" ? buildEmailInfoRow("Experience Required", experience) : ""}
              ${buildEmailInfoRow("Package/Budget", salary)}
              ${buildEmailInfoRow("Shift Timings", shift)}
            </tbody>
          </table>
          ${formattedRoles ? `
              <p style="margin: 12px 0 16px; padding-bottom: 8px; color: #0f3f6e; font-family: Arial, sans-serif; font-size: 15px; line-height: 22px; font-weight: 700;">
                <strong><span style="color: #0f3f6e; font-size: 16px;">Roles and Responsibilities:</span></strong>
              </p>
              <p style="margin: 0 0 12px; color: #111827; font-family: Arial, sans-serif; font-size: 15px; line-height: 23px; word-break: normal; overflow-wrap: break-word; white-space: normal;">
                ${formattedRoles}
              </p>
          ` : ""}
      `;
    }

        const signatureSpacing = includeJD ? "<br />" : "<br />";
        const finalEmailBody = removeEditorBackgrounds(`${templateBody || `<p style="margin: 0 0 16px;">${greeting}</p>`}${includeJD ? jobInfoHtml : ""}${signatureSpacing}${signatureToUse}`);
        const nextBody = linkifyEditorHtml(finalEmailBody.trim());
        setEmailtextEditor(prev => prev === nextBody ? prev : nextBody);
        const editor = emailHtmlEditorRef.current;
        if (emailviewpopSelected && editor?.core?.context?.element?.wysiwyg) {
            applyingGeneratedEmail.current = true;
            try {
                editor.setContents(nextBody);
            } finally {
                applyingGeneratedEmail.current = false;
            }
        }
        if (emailPreviewRef.current) emailPreviewRef.current.scrollTop = 0;
    }, [templateBody, signatureText, defaultSignature, emailviewpopSelected, emailviewpopAll, jobsallpopup, jobssearchedpopup, selectedCandidateCount, selectedSignature, jobData, selectedData, includeJD]);

const onJobsSearchInput = (e) => {
  setJobsSearchText(e.target.value);
  setJobsLazyParams({ ...jobsLazyParams, page: 0 }); // Reset to first page on search
};

useEffect(() => {
  if (jobDetailsVisible) {
    fetchJobs(jobsLazyParams.page, jobsLazyParams.rows);
  }
}, [jobDetailsVisible, jobsLazyParams]);

    return (
        <React.Fragment>
            <Toast ref={toast} />
            {showDropdown ? (
                <CascadeSelect
                    // value={selectedActEmail}
                    onChange={handleEmailChange}
                    options={actEmailOptions}
                    optionLabel="name"
                    optionGroupLabel="name"
                    optionGroupChildren={["subItems"]}
                    className="md:w-8rem me-1 ms-1"
                    placeholder="Email"
                />
            ) : (
                <Button
                    type="button"
                    label={buttonLabel}
                    icon="pi pi-envelope"
                    className={buttonClassName}
                    style={buttonStyle}
                    onClick={openSelectedEmailComposer}
                />
            )}

            <div className="email-dialog-host">
                {/* New email start */}
                <div>
                    <Dialog
                        header="Compose New Email To Candidate"
                        visible={emailviewpop}
                        className="interview-popup"
                        style={{ width: "75vw", height: "82%" }}
                        maximizable
                        modal
                        onHide={() => {
                            SetEmailviewpop(false);
                            setPrivateChecked(false);
                            // Reset all form fields
                            setFromemailjobs([]); // Reset to default user email
                            setTonewemail([]);
                            setCcemail([]);
                            setSubject(getDefaultEmailSubject());
                            setFromemailjobsSelectedTo([]);
                            setEmailtextEditor(`
  <p>Dear {{candidate_name}},</p>
  <br />
  ${defaultSignature}
      `);
                            // Reset all CC-related states
                            setCcemail([]);
                            setCcemailSelected([]);
                            setCcemailSearched([]);
                            setCcemailall([]);
                            setCcemailjobsall([]);
                            setCcemailjobsSearched([]);
                            setCcemailjobssear([]);
                            setAttachmentfile([]);
                            setSelectedTemplate(null);
                            const defaultSig = signatureOptions.find(signature => signature.is_default) || signatureOptions[0];
    setSelectedSignature(defaultSig || null);
    setSignatureText(defaultSig ? defaultSig.signature_text : (defaultSignature || ""));
    resetSignatureAndTemplate();
    
                            setFormErrors({});
                        }}
                        footer={footerContent}
                    >
                        <Row>
                            <div className="popup-header-bg">
                                {/* Template and signature stay in the header so the body stays focused on the message. */}
                                <Col lg={12}>
                                    <div className="d-flex">
                                        {/* <Dropdown
                                            value={selectedTemplate}
                                            onChange={handleTemplateChange}
                                            options={templateOptions}
                                            placeholder="Template"
                                            valueTemplate={selectedEmailTemplate1}
                                            itemTemplate={emailOptionTemplate1}
                                            className="w-full md:w-10rem action-items me-1"
                                            dropdownIcon={opts => {
                                                return opts.iconProps["data-pr-overlay-visible"] ? (
                                                    <ChevronRightIcon {...opts.iconProps} />
                                                ) : (
                                                    <ChevronDownIcon {...opts.iconProps} />
                                                );
                                            }}
                                        /> */}
                                        {/* <Dropdown
                                            value={selectedSignature}
                                            onChange={handleSignatureChange}
                                            options={signatureOptions}
                                            optionLabel="label"
                                            placeholder={signatureLoading ? "Loading signatures..." : signatureError || (signatureOptions.length ? "Signature" : "No email signature configured for this company.")}
                                            emptyMessage="No email signature configured for this company."
                                            valueTemplate={selectedSmsTemplate}
                                            itemTemplate={smsOptionTemplate}
                                            className="w-full md:w-10rem action-items me-1"
                                            dropdownIcon={opts => {
                                                return opts.iconProps["data-pr-overlay-visible"] ? (
                                                    <ChevronRightIcon {...opts.iconProps} />
                                                ) : (
                                                    <ChevronDownIcon {...opts.iconProps} />
                                                );
                                            }}
                                        /> */}
                                        <div className="d-inline-block">
                                            <FileUpload
                                                name="emailAttachments[]"
                                                ref={fileUploadRef}
                                                url={'/api/upload'}
                                                mode="basic"
                                                multiple
                                                accept="image/*,application/pdf"
                                                maxFileSize={10000000}
                                                auto
                                                chooseLabel={<span>Attach File</span>}
                                                customUpload
                                                uploadHandler={onUploadHandler1}
                                                className="me-2"
                                            />
                                        </div>
                                        {/* <Button className="preview-btn ms-auto" onClick={handleSendMail} disabled={isLoading}>
                                            <i className="pi pi-send me-1"></i> {isLoading ? 'Sending...' : 'Send'}
                                        </Button> */}
                                    </div>
                                </Col>
                            </div>
                        </Row>

                        <div className="bg-form form-insidepop">
                            <Row className="mb-2">
                                <Col xl={12}>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                            value={frominputemail}
                                            onChange={(e) => {
                                                applyFromEmailSelection(e?.value);
                                            }}
                                            options={fromEmail}
                                                    optionLabel="name"
                                                    placeholder="From"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={fromemailjobs}
                                                onChange={e => {
                                                    const newEntry = e.value[e.value.length - 1];
                                                    setFromemailjobs([newEntry]);
                                                }}
                                                itemTemplate={Fromemailcont}
                                                className="size-mail w-full email-recipient-chips"
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.from}</p>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail1}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setLinkCantoVisible(true);
                                                    }}
                                                    optionLabel="name"
                                                    placeholder="To"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={tonewemail}
                                                onChange={e => setTonewemail(e.value)}
                                                onClick={() => openRecipientPreview(tonewemail)}
                                                itemTemplate={item => (
                                                    <span onClick={() => openRecipientPreview(tonewemail)}>{item}</span>
                                                )}
                                                className="size-mail w-full to-recipient-chips"
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.to}</p>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail2}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setLinkCanccVisible(true);
                                                    }}
                                                    optionLabel="name"
                                                    placeholder="Cc"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={ccemailSelected}
                                                onChange={e => setCcemailSelected(e.value)}
                                                itemTemplate={Ccemailcont}
                                                className="size-mail w-full email-recipient-chips"
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <label htmlFor="interview">Subject:</label>
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <InputText
                                                id="interview"
                                                aria-describedby="username-help"
                                                placeholder=""
                                                className="w-full"
                                                value={subject}
                                                onChange={(e) => {
        setSubject(e.target.value);
        setFormErrors(prev => ({ ...prev, subject: e.target.value.trim() ? "" : prev.subject }));
    }}
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.subject}</p>
                                        </Col>
                                    </Row>

                                    {attachmentfile.length > 0 && (
                                        <Row className="size-mail mt-2 align-items-baseline">
                                            <Col xl={1}>
                                                <label>Attached:</label>
                                            </Col>
                                            <Col xl={11}>
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <div
                                                        ref={fileListRef}
                                                        style={{
                                                            display: "flex",
                                                            overflowX: "auto",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: "100%",
                                                            padding: "10px 0px",
                                                            borderRadius: "4px",
                                                        }}
                                                    >
                                                        {attachmentfile.map((file, index) => (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    marginRight: "15px",
                                                                    padding: "5px 10px",
                                                                    border: "1px solid #ddd",
                                                                    borderRadius: "4px",
                                                                    background: "#f9f9f9",
                                                                    boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                                                                }}
                                                            >
                                                                <i className="pi pi-file" style={{ marginRight: "5px" }}></i>
                                                                {renderAttachmentName(file, setPreviewFile)}
                                                                <i
                                                                    className="pi pi-times"
                                                                    style={{
                                                                        marginLeft: "8px",
                                                                        cursor: "pointer",
                                                                        color: "#ff0000",
                                                                    }}
                                                                    onClick={() => removeFile(file.file_name)}
                                                                ></i>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Col>
                            </Row>

                            <Row>
                                <Col xl={12}>
                                    <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.body}</p>
                                    <Editor
                                        value={emailtextEditor}
                                        onTextChange={e => setEmailtextEditor(e.htmlValue)}
                                        onBlur={() => setEmailtextEditor(prev => linkifyEditorHtml(prev))}
                                        onPaste={handleEditorPaste(setEmailtextEditor)}
                                        className="editorpage"
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Dialog>
                </div>
                {/* New email end */}

                {/* New email selected start */}
                {selectedData && selectedData.length > 0 ? (
                    emailviewpopSelected && (
                    <div className="email-compose-backdrop">
                        <div className="interview-popup email-compose-dialog email-compose-modal" role="dialog" aria-modal="true" aria-labelledby="selected-email-compose-title">
                            <div className="email-compose-modal-header">
                                <div>
                                    <h2 id="selected-email-compose-title">Send Job Emails to Selected Candidates</h2>
                                    <p>{selectedCandidateCount} selected candidate{selectedCandidateCount === 1 ? "" : "s"}</p>
                                </div>
                                <div className="email-compose-header-actions">
                                    <Dropdown
                                        value={selectedSignature}
                                        onChange={handleSignatureChange}
                                        options={signatureOptions}
                                        optionLabel="label"
                                        placeholder={signatureLoading ? "Loading signatures..." : signatureError || (signatureOptions.length ? "Signature" : "No email signature configured for this company.")}
                                        emptyMessage="No email signature configured for this company."
                                        valueTemplate={selectedSmsTemplate}
                                        itemTemplate={smsOptionTemplate}
                                        className="signature-dropdown"
                                        dropdownIcon={opts => {
                                            return opts.iconProps["data-pr-overlay-visible"] ? (
                                                <ChevronRightIcon {...opts.iconProps} />
                                            ) : (
                                                <ChevronDownIcon {...opts.iconProps} />
                                            );
                                        }}
                                    />
                                    <FileUpload
                                        name="emailAttachments[]"
                                        ref={fileUploadRef}
                                        url={'/api/upload'}
                                        mode="basic"
                                        multiple
                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                                        maxFileSize={10485760}
                                        onSelect={onUploadHandler1}
                                        onValidationFail={file => setFormErrors(prev => ({ ...prev, attachment: `File "${file.name}" exceeds the 10MB limit or has an unsupported format.` }))}
                                        chooseLabel={<span>Attach File</span>}
                                        customUpload
                                        uploadHandler={onUploadHandler1}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="email-compose-close"
                                    aria-label="Close"
                                    onClick={() => {
                                SetEmailviewpopSelected(false);
                                onClose?.(getJobCandidateIdsForEmails(toemailSelected));
                                // Reset all form fields
                                setFromemailjobs([]);
                                setTonewemail([]);
                                setIncludeJD(false);
                                setHideJdAttachmentChip(false);

                                setFromemailjobsSelectedTo([]); // Reset From email
                                setToemailSelected([]); // Reset To email
                                setCcemailSelected([]); // Reset CC email
                                setSubject(getDefaultEmailSubject()); // Reset Subject
                                setEmailtextEditor(`
  <p>Dear {{candidate_name}},</p>
  <br />
  ${defaultSignature}
                                    `); // Reset email body
                                // setAttachmentfile([]);
                                // Reset all CC-related states
                                setCcemail([]);
                                setCcemailSelected([]);
                                setCcemailSearched([]);
                                setCcemailall([]);
                                setCcemailjobsall([]);
                                setCcemailjobsSearched([]);
                                setCcemailjobssear([]);
                                setSelectedTemplate(null); // Reset template
                                const defaultSig = signatureOptions.find(signature => signature.is_default) || signatureOptions[0];
    setSelectedSignature(defaultSig || null); // Reset signature
    setSignatureText(defaultSig ? defaultSig.signature_text : (defaultSignature || ""));
                                setPrivateChecked(false); // Reset private checkbox
                                setFormErrors({}); // Reset form errors
                                resetSignatureAndTemplate();
                            }}
                        >
                                    <i className="pi pi-times"></i>
                                </button>
                            </div>
                            <div className="email-compose-modal-body">
                            <div className="email-compose-shell">
                            <div className="email-compose-main">
                            <div className="email-compose-left-panel">
                            <div className="bg-form form-insidepop">
                                <Row className="mb-2">
                                    <Col xl={12}>
                                        <div
                                            className="email-inline-fields"
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "minmax(240px, 0.9fr) minmax(260px, 1fr) minmax(220px, 0.8fr) minmax(300px, 1.15fr)",
                                                gap: 12,
                                                alignItems: "start",
                                                overflowX: "visible",
                                                paddingBottom: 2,
                                            }}
                                        >
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
                                                    <Dropdown
                                                        onChange={(e) => {
                                                            applyFromEmailSelection(e?.value);
                                                        }}
                                                        options={fromEmail}
                                                        optionLabel="name"
                                                        placeholder="From"
                                                        className="from-width"
                                                        style={{ width: 92, flex: "0 0 92px" }}
                                                    />
                                                    <Chips
                                                        value={fromemailjobsSelectedTo}
                                                        onChange={e => {
                                                            const newEntry = e.value[e.value.length - 1];
                                                            setFromemailjobsSelectedTo([newEntry]);
                                                        }}
                                                        disabled
                                                        itemTemplate={Fromemailcont}
                                                        className="size-mail w-full email-recipient-chips"
                                                        style={{ minWidth: 0, flex: 1 }}
                                                    />
                                                </div>
                                                <p style={{ color: 'red', fontSize: '13px', margin: 0 }}>{formErrors.from}</p>
                                            </div>

                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
                                                    <Dropdown
                                                        value={frominputemail1}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setLinkCantoVisible(true);
                                                        }}
                                                        optionLabel="name"
                                                        placeholder="To"
                                                        className="from-width"
                                                        style={{ width: 76, flex: "0 0 76px" }}
                                                    />
                                                    <Chips
                                                        value={toemailSelected}
                                                        onChange={e => setToemailSelected(e.value)}
                                                        onClick={() => openRecipientPreview(toemailSelected)}
                                                        itemTemplate={item => (
                                                            <span onClick={() => openRecipientPreview(toemailSelected)}>{item}</span>
                                                        )}
                                                        className="size-mail w-full to-recipient-chips"
                                                        style={{ minWidth: 0, flex: 1 }}
                                                    />
                                                </div>
                                                <p style={{ color: 'red', fontSize: '13px', margin: 0 }}>{formErrors.to}</p>
                                            </div>

                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
                                                    <Dropdown
                                                        value={frominputemail2}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setLinkCanccVisible(true);
                                                        }}
                                                        optionLabel="name"
                                                        placeholder="Cc"
                                                        className="from-width"
                                                        style={{ width: 76, flex: "0 0 76px" }}
                                                    />
                                                    <Chips
                                                        value={ccemailSelected}
                                                        onChange={e => setCcemailSelected(e.value)}
                                                        itemTemplate={Ccemailcont}
                                                        className="size-mail w-full email-recipient-chips"
                                                        style={{ minWidth: 0, flex: 1 }}
                                                    />
                                                </div>
                                            </div>

                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ display: "flex", gap: 8, minWidth: 0 }}>
                                                    <label
                                                        htmlFor="interview"
                                                        style={{
                                                            width: 78,
                                                            flex: "0 0 78px",
                                                            height: 38,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            border: "1px solid #d1d5db",
                                                            borderRadius: 4,
                                                            margin: 0,
                                                        }}
                                                    >
                                                        Subject:
                                                    </label>
                                                    <InputText
                                                        id="interview"
                                                        aria-describedby="username-help"
                                                        placeholder=""
                                                        className="w-full"
                                                        value={subject}
                                                        onChange={(e) => {
                                                            setSubject(e.target.value);
                                                            setFormErrors(prev => ({ ...prev, subject: e.target.value.trim() ? "" : prev.subject }));
                                                        }}
                                                        style={{ minWidth: 0, flex: 1 }}
                                                    />
                                                </div>
                                                <p style={{ color: 'red', fontSize: '13px', margin: 0 }}>{formErrors.subject}</p>
                                            </div>
                                        </div>

                                        <Row className="size-mail mt-2 align-items-baseline email-compose-attachments">
                                            <Col xl={12}>
                                                <div className="email-attachment-panel">
                                                    <label className="email-attachment-label">Attached:</label>
                                                    {attachmentFilesForDisplay.length > 0 ? (
                                                        <div
                                                            ref={fileListRef}
                                                            className="email-attachment-list"
                                                        >
                                                            {attachmentFilesForDisplay.map((file, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="email-attachment-chip"
                                                                >
                                                                    <i className="pi pi-file"></i>
                                                                    {renderAttachmentName(file, setPreviewFile)}
                                                                    <i
                                                                        className="pi pi-times"
                                                                        onClick={() => {
                                                                            if (file.display_only) {
                                                                                setHideJdAttachmentChip(true);
                                                                                setIncludeJD(false);
                                                                                return;
                                                                            }
                                                                            removeFile(file.file_name);
                                                                        }}
                                                                    ></i>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="email-no-attachments">No files attached</span>
                                                    )}
                                                    <div className="email-include-jd">
                                                        <Checkbox
                                                            inputId="includeJD"
                                                            checked={includeJD}
                                                            onChange={e => {
                                                                setIncludeJD(e.checked);
                                                                if (e.checked) {
                                                                    setHideJdAttachmentChip(false);
                                                                }
                                                            }}
                                                        />
                                                        <label htmlFor="includeJD">Include JD</label>
                                                    </div>
                                                </div>
                                                {formErrors.attachment && <div role="alert" style={{ color: "#dc3545", padding: "4px 14px" }}>{formErrors.attachment}</div>}
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                <Row className="email-compose-editor-row">
                                    <Col xl={12}>
                                        <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.body}</p>
                                        <div className="editorpage email-html-editor">
                                            <SunEditor
                                                getSunEditorInstance={editor => {
                                                    emailHtmlEditorRef.current = editor;
                                                    protectEmailEditorCleanup(editor);
                                                }}
                                                defaultValue={emailtextEditor || ""}
                                                autoFocus={false}
                                                onChange={content => {
                                                    if (!applyingGeneratedEmail.current && emailHtmlEditorRef.current?.core?.hasFocus) {
                                                        setEmailtextEditor(content);
                                                    }
                                                }}
                                                setOptions={{
                                                    height: "350px",
                                                    minHeight: "250px",
                                                    defaultStyle: "font-family: Arial, sans-serif; font-size: 15px; line-height: 1.5;",
                                                    buttonList: [
                                                        ["undo", "redo"],
                                                        ["font", "fontSize"],
                                                        ["bold", "italic", "underline", "fontColor"],
                                                        ["align", "list", "link", "table", "removeFormat"]
                                                    ]
                                                }}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                            </div>
                            <aside className="email-compose-preview-panel">
                                <div className="email-preview-label">Preview</div>
                                <div className="email-preview-card">
                                    <div className="email-preview-subject">{subject || "Email subject"}</div>
                                    <div className="email-preview-meta">
                                        From {fromemailjobsSelectedTo?.[0] || "sender"} · to {getCandidateGreetingName()}
                                    </div>
                                    <div className="email-preview-divider" />
                                    <div
                                        className="email-preview-body"
                                        ref={emailPreviewRef}
                                        dangerouslySetInnerHTML={{ __html: emailtextEditor || "" }}
                                    />
                                </div>
                            </aside>
                            </div>
                            </div>
                            </div>
                            <div className="email-compose-modal-footer">
                                {footerContent}
                            </div>
                        </div>
                    </div>
                    )
                ) : (
                    <Dialog
                        header="No Candidates Selected"
                        visible={emailviewpopSelected}
                        style={{ width: "30vw" }}
                        modal
                        onHide={() => SetEmailviewpopSelected(false)}
                        footer={
                            <div>
                                <Button
                                    label="OK"
                                    icon="pi pi-check"
                                    onClick={() => SetEmailviewpopSelected(false)}
                                    className="p-button-primary"
                                />
                            </div>
                        }
                    >
                        <div className="p-2">
                            <p>Please select at least one candidate to send job emails.</p>
                        </div>
                    </Dialog>
                )}
                {/* New email selected end */}


                {/* New email searched start */}
                <div>
                    <Dialog
                        header="Send Job Emails to Searched Candidates"
                        visible={emailviewpopSearched}
                        className="interview-popup"
                        style={{ width: "75vw", height: "82%" }}
                        maximizable
                        modal
                        onHide={() => {
                            SetEmailviewpopSearched(false);
                            // Reset all form fields
                            setFrominputemail(null);
                            setFromemailjobsSelectedTo([]);
                            setToemailSearched([]);
                            setCcemailSearched([]);
                            setSubject(getDefaultEmailSubject());
                            // Reset all CC-related states
                            setCcemail([]);
                            setCcemailSelected([]);
                            setCcemailSearched([]);
                            setCcemailall([]);
                            setCcemailjobsall([]);
                            setCcemailjobsSearched([]);
                            setCcemailjobssear([]);
                            setEmailtextEditor(`
  <p>Dear {{candidate_name}},</p>
  <br />
  ${defaultSignature}
      `);
                            setAttachmentfile([]);
                            setSelectedTemplate(null);
                                const defaultSig = signatureOptions.find(signature => signature.is_default) || signatureOptions[0];
    setSelectedSignature(defaultSig || null);
    setSignatureText(defaultSig ? defaultSig.signature_text : (defaultSignature || ""));
                            setPrivateChecked(false);
                            setFormErrors({});
                            resetSignatureAndTemplate();
                            // Reset candidate selections
                            setSelectedCanto([]);
                            setSelectedCancc([]);
                            // Clear file upload component
                            if (fileUploadRef.current) {
                                fileUploadRef.current.clear();
                            }
                        }}
                        footer={footerContent}
                    >
                        <Row>
                            <div className="popup-header-bg">
                                <Col lg={12} className="d-flex align-items-center">
                                    {/* <Dropdown
                                        value={selectedTemplate}
                                        onChange={handleTemplateChange}
                                        options={templateOptions}
                                        placeholder="Template"
                                        valueTemplate={selectedEmailTemplate1}
                                        itemTemplate={emailOptionTemplate1}
                                        className="w-full md:w-10rem me-2"
                                        dropdownIcon={opts => {
                                            return opts.iconProps["data-pr-overlay-visible"] ? (
                                                <ChevronRightIcon {...opts.iconProps} />
                                            ) : (
                                                <ChevronDownIcon {...opts.iconProps} />
                                            );
                                        }}
                                    />
                                    <Dropdown
                                        value={selectedSignature}
                                        onChange={handleSignatureChange}
                                        options={signatureOptions}
                                        optionLabel="label"
                                        placeholder={signatureLoading ? "Loading signatures..." : signatureError || (signatureOptions.length ? "Signature" : "No email signature configured for this company.")}
                                        emptyMessage="No email signature configured for this company."
                                        valueTemplate={selectedSmsTemplate}
                                        itemTemplate={smsOptionTemplate}
                                        className="w-full md:w-10rem me-2 signature-dropdown"
                                        dropdownIcon={opts => {
                                            return opts.iconProps["data-pr-overlay-visible"] ? (
                                                <ChevronRightIcon {...opts.iconProps} />
                                            ) : (
                                                <ChevronDownIcon {...opts.iconProps} />
                                            );
                                        }}
                                    /> */}
                                    <div className="d-inline-block">
                                        <FileUpload
                                            name="emailAttachments[]"
                                            ref={fileUploadRef}
                                            url={'/api/upload'}
                                            mode="basic"
                                            multiple
                                            accept="image/*,application/pdf"
                                            maxFileSize={5000000}
                                            auto
                                            chooseLabel={<span>Attach File</span>}
                                            customUpload
                                            uploadHandler={onUploadHandler1}
                                            className="me-2"
                                        />
                                    </div>
                                    {/* <Button className="preview-btn ms-auto" onClick={handleSendMail} disabled={isLoading}>
                                        <i className="pi pi-send me-1"></i> {isLoading ? 'Sending...' : 'Send'}
                                    </Button> */}
                                </Col>
                            </div>
                        </Row>

                        <div className="bg-form form-insidepop">
                            <Row className="mb-2">
                                <Col xl={12}>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail}
                                                    onChange={(e) => {
                                                        applyFromEmailSelection(e?.value);
                                                    }}
                                                    options={fromEmail}
                                                    optionLabel="name"
                                                    placeholder="From"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={fromemailjobsSelectedTo}
                                                onChange={e => {
                                                    const newEntry = e.value[e.value.length - 1];
                                                    setFromemailjobsSelectedTo([newEntry]);
                                                }}
                                                disabled
                                                itemTemplate={Fromemailcont}
                                                className="size-mail w-full email-recipient-chips"
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.from}</p>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail1}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setLinkCantoVisible(true);
                                                    }}
                                                    optionLabel="name"
                                                    placeholder="To"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={toemailSearched}
                                                onChange={e => setToemailSearched(e.value)}
                                                onClick={() => openRecipientPreview(toemailSearched)}
                                                itemTemplate={item => (
                                                    <span onClick={() => openRecipientPreview(toemailSearched)}>{item}</span>
                                                )}
                                                className="size-mail w-full to-recipient-chips"
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.to}</p>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                    <Dropdown
                                                    value={frominputemail2}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setLinkCanccVisible(true);
                                                    }}
                                                    optionLabel="name"
                                                    placeholder="Cc"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={ccemailSearched}
                                                onChange={e => setCcemailSearched(e.value)}
                                                itemTemplate={Ccemailcont}
                                                className="size-mail w-full email-recipient-chips"
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <label htmlFor="interview">Subject:</label>
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <InputText
                                                id="interview"
                                                aria-describedby="username-help"
                                                placeholder=""
                                                className="w-full"
                                                value={subject}
                                                onChange={(e) => {
                                                    setSubject(e.target.value);
                                                    setFormErrors(prev => ({ ...prev, subject: e.target.value.trim() ? "" : prev.subject }));
                                                }}
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.subject}</p>
                                        </Col>
                                    </Row>

                                    {attachmentfile.length > 0 && (
                                        <Row className="size-mail mt-2 align-items-baseline">
                                            <Col xl={1}>
                                                <label>Attached:</label>
                                            </Col>
                                            <Col xl={11}>
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <div
                                                        ref={fileListRef}
                                                        style={{
                                                            display: "flex",
                                                            overflowX: "auto",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: "100%",
                                                            padding: "10px 0px",
                                                            borderRadius: "4px",
                                                        }}
                                                    >
                                                        {attachmentfile.map((file, index) => (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    marginRight: "15px",
                                                                    padding: "5px 10px",
                                                                    border: "1px solid #ddd",
                                                                    borderRadius: "4px",
                                                                    background: "#f9f9f9",
                                                                    boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                                                                }}
                                                            >
                                                                <i className="pi pi-file" style={{ marginRight: "5px" }}></i>
                                                                {renderAttachmentName(file, setPreviewFile)}
                                                                <i
                                                                    className="pi pi-times"
                                                                    style={{
                                                                        marginLeft: "8px",
                                                                        cursor: "pointer",
                                                                        color: "#ff0000",
                                                                    }}
                                                                    onClick={() => removeFile(file.file_name)}
                                                                ></i>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Col>
                            </Row>

                            <Row>
                                <Col xl={12}>
                                    <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.body}</p>
                                    <Editor
                                        value={emailtextEditor}
                                        onTextChange={e => setEmailtextEditor(e.htmlValue)}
                                        onBlur={() => setEmailtextEditor(prev => linkifyEditorHtml(prev))}
                                        className="editorpage"
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Dialog>
                </div>
                {/* New email searched end */}


                {/* New email All start */}
                <div>
                    <Dialog
                        header="Send Job Emails to All Candidates"
                        visible={emailviewpopAll}
                        className="interview-popup"
                        style={{ width: "75vw", height: "82%" }}
                        maximizable
                        modal
                        onHide={() => {
                            SetEmailviewpopAll(false);
                            setPrivateChecked(false);
                            setFromemailjobs([]);
                            setTonewemail([]);
                            setCcemail([]);
                            setSubject(getDefaultEmailSubject());
                            setEmailtextEditor(`
  <p>Dear {{candidate_name}},</p>
  <br />
  ${defaultSignature}
        `);
                            setAttachmentfile([]);
                            // Reset all CC-related states
                            setCcemail([]);
                            setCcemailSelected([]);
                            setCcemailSearched([]);
                            setCcemailall([]);
                            setCcemailjobsall([]);
                            setCcemailjobsSearched([]);
                            setCcemailjobssear([]);
                            setSelectedTemplate(null);
                            resetSignatureAndTemplate();
                                        const defaultSig = signatureOptions.find(signature => signature.is_default) || signatureOptions[0];
                            setSignatureText(defaultSig ? defaultSig.signature_text : (defaultSignature || ""));
    setSelectedSignature(defaultSig || null);
                            setFormErrors({});
                            setCcemailSelected([])
                        }}
                        footer={footerContent}
                    >
                        <Row>
                            <div className="popup-header-bg">
                                <Col lg={12} className="d-flex align-items-center">
                                    {/* <Dropdown
                                        value={selectedTemplate}
                                        onChange={handleTemplateChange}
                                        options={templateOptions}
                                        placeholder="Template"
                                        valueTemplate={selectedEmailTemplate1}
                                        itemTemplate={emailOptionTemplate1}
                                        className="w-full md:w-10rem me-2 signature-dropdown"
                                        dropdownIcon={opts => {
                                            return opts.iconProps["data-pr-overlay-visible"] ? (
                                                <ChevronRightIcon {...opts.iconProps} />
                                            ) : (
                                                <ChevronDownIcon {...opts.iconProps} />
                                            );
                                        }}
                                    />
                                    <Dropdown
                                        value={selectedSignature}
                                        onChange={handleSignatureChange}
                                        options={signatureOptions}
                                        optionLabel="label"
                                        placeholder={signatureLoading ? "Loading signatures..." : signatureError || (signatureOptions.length ? "Signature" : "No email signature configured for this company.")}
                                        emptyMessage="No email signature configured for this company."
                                        valueTemplate={selectedSmsTemplate}
                                        itemTemplate={smsOptionTemplate}
                                        className="w-full md:w-10rem me-2"
                                        dropdownIcon={opts => {
                                            return opts.iconProps["data-pr-overlay-visible"] ? (
                                                <ChevronRightIcon {...opts.iconProps} />
                                            ) : (
                                                <ChevronDownIcon {...opts.iconProps} />
                                            );
                                        }}
                                    /> */}
                                    <div className="d-inline-block">
                                        <FileUpload
                                            name="emailAttachments[]"
                                            ref={fileUploadRef}
                                            url={'/api/upload'}
                                            mode="basic"
                                            multiple
                                            accept="image/*,application/pdf"
                                            maxFileSize={10000000}
                                            auto
                                            chooseLabel={<span>Attach File</span>}
                                            customUpload
                                            uploadHandler={onUploadHandler1}
                                            className="me-2"
                                        />
                                    </div>
                                    {/* <Button className="preview-btn ms-auto" onClick={handleSendMail} disabled={isLoading}>
                                        <i className="pi pi-send me-1"></i> {isLoading ? 'Sending...' : 'Send'}
                                    </Button> */}
                                </Col>
                            </div>
                        </Row>
                        <div className="bg-form form-insidepop">
                            <Row className="mb-2">
                                <Col xl={12}>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail}
                                                    onChange={(e) => {
                                                        applyFromEmailSelection(e?.value);
                                                    }}
                                                    options={fromEmail}
                                                    optionLabel="name"
                                                    placeholder="From"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={fromemailjobs}
                                                onChange={e => {
                                                    const newEntry = e.value[e.value.length - 1];
                                                    setFromemailjobs([newEntry]);
                                                }}
                                                itemTemplate={Fromemailcont}
                                                className="size-mail w-full email-recipient-chips"
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.from}</p>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail1}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                    }}
                                                    optionLabel="name"
                                                    placeholder="To"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={["All Candidates Selected"]}
                                                onClick={() => openRecipientPreview(["All Candidates Selected"])}
                                                itemTemplate={item => (
                                                    <span onClick={() => openRecipientPreview(["All Candidates Selected"])}>{item}</span>
                                                )}
                                                className="size-mail w-full to-recipient-chips"
                                                disabled
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail2}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setLinkCanccVisible(true);
                                                    }}
                                                    optionLabel="name"
                                                    placeholder="Cc"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={ccemailall}
                                                onChange={(e) => setCcemailall(e.value)}
                                                itemTemplate={Ccemailcont}
                                                className="size-mail w-full email-recipient-chips"
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <label htmlFor="interview">Subject:</label>
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <InputText
                                                id="interview"
                                                aria-describedby="username-help"
                                                placeholder=""
                                                className="w-full"
                                                value={subject}
                                                onChange={(e) => {
                                                    setSubject(e.target.value);
                                                    setFormErrors(prev => ({ ...prev, subject: e.target.value.trim() ? "" : prev.subject }));
                                                }}
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.subject}</p>
                                        </Col>
                                    </Row>
                                    {attachmentfile.length > 0 && (
                                        <Row className="size-mail mt-2 align-items-baseline">
                                            <Col xl={1}>
                                                <label>Attached:</label>
                                            </Col>
                                            <Col xl={11}>
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <div
                                                        ref={fileListRef}
                                                        style={{
                                                            display: "flex",
                                                            overflowX: "auto",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: "100%",
                                                            padding: "10px 0px",
                                                            borderRadius: "4px",
                                                        }}
                                                    >
                                                        {attachmentfile.map((file, index) => (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    marginRight: "15px",
                                                                    padding: "5px 10px",
                                                                    border: "1px solid #ddd",
                                                                    borderRadius: "4px",
                                                                    background: "#f9f9f9",
                                                                    boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                                                                }}
                                                            >
                                                                <i className="pi pi-file" style={{ marginRight: "5px" }}></i>
                                                                {renderAttachmentName(file, setPreviewFile)}
                                                                <i
                                                                    className="pi pi-times"
                                                                    style={{
                                                                        marginLeft: "8px",
                                                                        cursor: "pointer",
                                                                        color: "#ff0000",
                                                                    }}
                                                                    onClick={() => removeFile(file.file_name)}
                                                                ></i>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col xl={12}>
                                    <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.body}</p>
                                    <Editor
                                        value={emailtextEditor}
                                        onTextChange={(e) => setEmailtextEditor(e.htmlValue)}
                                        onBlur={() => setEmailtextEditor(prev => linkifyEditorHtml(prev))}
                                        className="editorpage"
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Dialog>

                </div>
                {/* New email All end */}


                {/* jobs all start */}
                <Dialog
                    header="Send an Email to the Candidate with All Jobs"
                    visible={jobsallpopup}
                    className="interview-popup"
                    onHide={() => {
                        SetJobsallPopup(false);
                        // Reset form fields
                        setFrominputemail(null);
                        setFromemailjobs([]);
                        setToemailjobs([]);
                        setCcemailjobsall([]);
                        setSubject(getDefaultEmailSubject());
                        setJobDescriptions(`
      <p>Dear {{candidate_name}}</p>
      <br />
     ${defaultSignature}
    `);
                        setAttachmentfile([]);
                        setSelectedTemplate(null);
                        const defaultSig = signatureOptions.find(signature => signature.is_default) || signatureOptions[0];
                        setSignatureText(defaultSig ? defaultSig.signature_text : (defaultSignature || ""));
    setSelectedSignature(defaultSig || null);
                        setPrivateChecked(false);
                        setFormErrors({});
                        resetSignatureAndTemplate();
                        setSelectedCanto([]);
                        setSelectedCancc([]);
                        if (fileUploadRef.current) {
                            fileUploadRef.current.clear();
                        }
                    }}
                    style={{ width: "75vw", height: "82%" }}
                    maximizable
                    modal
                    footer={footerContent}
                >
                    <Row>
                        <div className="popup-header-bg">
                            <Col lg={12} className="d-flex align-items-center">
                                {/* <Dropdown
                                    value={selectedTemplate}
                                    onChange={handleTemplateChange}
                                    options={templateOptions}
                                    placeholder="Template"
                                    valueTemplate={selectedEmailTemplate1}
                                    itemTemplate={emailOptionTemplate1}
                                    className="w-full md:w-10rem me-2"
                                    dropdownIcon={opts =>
                                        opts.iconProps["data-pr-overlay-visible"] ? (
                                            <ChevronRightIcon {...opts.iconProps} />
                                        ) : (
                                            <ChevronDownIcon {...opts.iconProps} />
                                        )
                                    }
                                />
                                <Dropdown
                                    value={selectedSignature}
                                    onChange={handleSignatureChange}
                                    options={signatureOptions}
                                    optionLabel="label"
                                    placeholder={signatureLoading ? "Loading signatures..." : signatureError || (signatureOptions.length ? "Signature" : "No email signature configured for this company.")}
                                    emptyMessage="No email signature configured for this company."
                                    valueTemplate={selectedSmsTemplate}
                                    itemTemplate={smsOptionTemplate}
                                    className="w-full md:w-10rem me-2 signature-dropdown"
                                    dropdownIcon={opts =>
                                        opts.iconProps["data-pr-overlay-visible"] ? (
                                            <ChevronRightIcon {...opts.iconProps} />
                                        ) : (
                                            <ChevronDownIcon {...opts.iconProps} />
                                        )
                                    }
                                /> */}
                                <div className="d-inline-block">
                                    <FileUpload
                                        name="emailAttachments[]"
                                        ref={fileUploadRef}
                                        url={'/api/upload'}
                                        mode="basic"
                                        multiple
                                        accept="image/*,application/pdf"
                                        maxFileSize={10000000}
                                        auto
                                        chooseLabel={<span>Attach File</span>}
                                        customUpload
                                        uploadHandler={onUploadHandler1}
                                        className="me-2"
                                    />
                                </div>
                                {/* <Button className="preview-btn ms-auto" onClick={handleSendMail} disabled={isLoading}>
                                    <i className="pi pi-send me-1"></i> {isLoading ? 'Sending...' : 'Send'}
                                </Button> */}
                            </Col>
                        </div>
                    </Row>

                    <div className="bg-form form-insidepop">
                        <Row className="mb-2">
                            <Col xl={12}>
                                <Row className="mb-2">
                                    <Col xl={1} className="pe-0">
                                        <div className="label-input">
                                            <Dropdown
                                                value={frominputemail}
                                                onChange={(e) => {
                                                    applyFromEmailSelection(e?.value);
                                                }}
                                                options={fromEmail}
                                                optionLabel="name"
                                                placeholder="From"
                                                className="from-width w-full"
                                            />
                                        </div>
                                    </Col>
                                    <Col xl={11}>
                                        <Chips
                                            value={fromemailjobs}
                                            onChange={e => {
                                                const newEntry = e.value[e.value.length - 1];
                                                setFromemailjobs([newEntry]);
                                                setFromemailjobsSelectedTo([newEntry]);
                                            }}
                                            itemTemplate={Fromemailcontjobs}
                                            className="size-mail w-full email-recipient-chips"
                                        />
                                        <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.from}</p>
                                    </Col>
                                </Row>

                                <Row className="mb-2">
                                    <Col xl={1} className="pe-0">
                                        <div className="label-input">
                                            <Dropdown
                                                value={frominputemail1}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                }}
                                                optionLabel="name"
                                                placeholder="To"
                                                className="from-width w-full"
                                            />
                                        </div>
                                    </Col>
                                    <Col xl={11}>
                                        <Chips
                                            value={["All Candidates Selected"]}
                                            onClick={() => openRecipientPreview(["All Candidates Selected"])}
                                            itemTemplate={item => (
                                                <span onClick={() => openRecipientPreview(["All Candidates Selected"])}>{item}</span>
                                            )}
                                            className="size-mail w-full to-recipient-chips"
                                            disabled
                                        />
                                    </Col>
                                </Row>

                                <Row className="mb-2">
                                    <Col xl={1} className="pe-0">
                                        <div className="label-input">
                                            <Dropdown
                                                value={frominputemail2}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setLinkCanccVisible(true);
                                                }}
                                                optionLabel="name"
                                                placeholder="Cc"
                                                className="from-width w-full"
                                            />
                                        </div>
                                    </Col>
                                    <Col xl={11}>
                                        <Chips
                                            value={ccemailjobsall}
                                            onChange={e => setCcemailjobsall(e.value)}
                                            itemTemplate={Ccemailcontjobs}
                                            className="size-mail w-full email-recipient-chips"
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xl={1} className="pe-0 mb-1">
                                        <div className="label-input">
                                            <label htmlFor="interview">Subject:</label>
                                        </div>
                                    </Col>
                                    <Col xl={11} className="mb-1">
                                        <InputText
                                            id="interview"
                                            aria-describedby="username-help"
                                            placeholder=""
                                            className="w-full"
                                            value={subject}
                                            onChange={(e) => {
                                                setSubject(e.target.value);
                                                setFormErrors(prev => ({ ...prev, subject: e.target.value.trim() ? "" : prev.subject }));
                                            }}
                                        />
                                        <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.subject}</p>
                                    </Col>
                                </Row>

                                {attachmentfile.length > 0 && (
                                    <Row className="size-mail mt-2 align-items-baseline">
                                        <Col xl={1}>
                                            <label>Attached:</label>
                                        </Col>
                                        <Col xl={11}>
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                <div
                                                    ref={fileListRef}
                                                    style={{
                                                        display: "flex",
                                                        overflowX: "auto",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: "100%",
                                                        padding: "10px 0px",
                                                        borderRadius: "4px",
                                                    }}
                                                >
                                                    {attachmentfile.map((file, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                marginRight: "15px",
                                                                padding: "5px 10px",
                                                                border: "1px solid #ddd",
                                                                borderRadius: "4px",
                                                                background: "#f9f9f9",
                                                                boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                                                            }}
                                                        >
                                                            <i className="pi pi-file" style={{ marginRight: "5px" }}></i>
                                                            {renderAttachmentName(file, setPreviewFile)}
                                                            <i
                                                                className="pi pi-times"
                                                                style={{
                                                                    marginLeft: "8px",
                                                                    cursor: "pointer",
                                                                    color: "#ff0000",
                                                                }}
                                                                onClick={() => removeFile(file.file_name)}
                                                            ></i>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col xl={12}>
                                <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.body}</p>
                                <Editor
                                    value={jobDescriptions}
                                    onTextChange={e => setJobDescriptions(e.htmlValue)}
                                    onPaste={handleEditorPaste(setJobDescriptions)}
                                    className="jobsall-editor"
                                />
                            </Col>
                        </Row>
                    </div>
                </Dialog>

                <Dialog
                    header="Job Details"
                    visible={jobDetailsVisible}
                    onHide={() => { setJobDetailsVisible(false); setSelectedJobs([]) }}
                    style={{ width: "40vw" }}
                    breakpoints={{ "960px": "75vw", "641px": "100vw" }}
                    className="cand-details"
                >
                    {/* Search Bar */}
                    <Row>
                        <Col lg={12}>
                            <div
                                className="p-inputgroup mb-3"
                                style={{ position: "relative" }}
                            >
                                <InputText
                                    placeholder="Search Jobs"
                                    value={jobsSearchText}
                                    onInput={onJobsSearchInput}
                                    style={{ paddingRight: "2rem" }} // Space for the icon
                                />
                                <i
                                    className="pi pi-search"
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "49%",
                                        transform: "translateY(-50%)",
                                    }}
                                ></i>
                            </div>
                        </Col>
                    </Row>

                    {/* DataTable */}
                    <DataTable
  value={filteredJobs}
  paginator
  lazy
  loading={jobsLoading}
  rows={jobsLazyParams.rows}
  first={jobsLazyParams.page * jobsLazyParams.rows}
  totalRecords={jobsTotalRecords}
  selection={selectedJobs}
  onPage={onJobsPageChange}
  onSelectionChange={e => setSelectedJobs(e.value)}
  dataKey="job_id"
  rowsPerPageOptions={[5, 10, 25, 50]}
  size="small"
  emptyMessage={jobsLoading ? "Loading..." : "No jobs found."}
>
  <Column selectionMode="multiple" style={{ width: "3em" }} />
  <Column field="job_number" header="Job ID" />
  <Column field="job_title" header="Job Title" />
  <Column field="hiring_manager_name" header="Contact" />
</DataTable>

                    {/* Actions */}
                    <div className="d-flex mt-3">
                        <button
                            type="button"
                            className="btn btn-primary me-2 btn-main"
                            onClick={handleSave}
                        >
                            Ok
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-main"
                            onClick={handleRefresh}
                        >
                            Refresh
                        </button>
                    </div>
                </Dialog>
                {/* jobs all end */}


                {/* Jobs Searched Start */}
                <Dialog
                    header="Send Job Emails to Searched Candidates"
                    visible={jobssearchedpopup}
                    className="interview-popup"
                    style={{ width: "75vw", height: "82%" }}
                    maximizable
                    modal
                    onHide={() => {
                        SetJobssearchedPopup(false);
                        // Reset all form fields
                        setFrominputemail(null);
                        setFromemailjobsSelectedTo([]);
                        setToemailsearchjobs([]);
                        setCcemailjobsSearched([]);
                        setSubject(getDefaultEmailSubject());
                        setJobDescriptions(`
        <p>Dear {{candidate_name}}</p>
        <br />
        ${defaultSignature}
      `);
                        setAttachmentfile([]);
                        setSelectedTemplate(null);
                        const defaultSig = signatureOptions.find(signature => signature.is_default) || signatureOptions[0];
                        setSignatureText(defaultSig ? defaultSig.signature_text : (defaultSignature || ""));
    setSelectedSignature(defaultSig || null);
                        setPrivateChecked(false);
                        setFormErrors({});
                        setSelectedCanto([]);
                        setSelectedCancc([]);
                        resetSignatureAndTemplate();
                        if (fileUploadRef.current) {
                            fileUploadRef.current.clear();
                        }
                    }}
                    footer={footerContent}
                >
                    <Row>
                        <div className="popup-header-bg">
                            <Col lg={12} className="d-flex align-items-center">
                                {/* <Dropdown
                                    value={selectedTemplate}
                                    onChange={handleTemplateChange}
                                    options={templateOptions}
                                    placeholder="Template"
                                    valueTemplate={selectedEmailTemplate1}
                                    itemTemplate={emailOptionTemplate1}
                                    className="w-full md:w-10rem me-2 signature-dropdown"
                                    dropdownIcon={opts =>
                                        opts.iconProps["data-pr-overlay-visible"] ? (
                                            <ChevronRightIcon {...opts.iconProps} />
                                        ) : (
                                            <ChevronDownIcon {...opts.iconProps} />
                                        )
                                    }
                                />
                                <Dropdown
                                    value={selectedSignature}
                                    onChange={handleSignatureChange}
                                    options={signatureOptions}
                                    optionLabel="label"
                                    placeholder={signatureLoading ? "Loading signatures..." : signatureError || (signatureOptions.length ? "Signature" : "No email signature configured for this company.")}
                                    emptyMessage="No email signature configured for this company."
                                    valueTemplate={selectedSmsTemplate}
                                    itemTemplate={smsOptionTemplate}
                                    className="w-full md:w-10rem me-2"
                                    dropdownIcon={opts =>
                                        opts.iconProps["data-pr-overlay-visible"] ? (
                                            <ChevronRightIcon {...opts.iconProps} />
                                        ) : (
                                            <ChevronDownIcon {...opts.iconProps} />
                                        )
                                    }
                                /> */}
                                <div className="d-inline-block">
                                    <FileUpload
                                        name="emailAttachments[]"
                                        ref={fileUploadRef}
                                        url={'/api/upload'}
                                        mode="basic"
                                        multiple
                                        accept="image/*,application/pdf"
                                        maxFileSize={10000000}
                                        auto
                                        chooseLabel={<span>Attach File</span>}
                                        customUpload
                                        uploadHandler={onUploadHandler1}
                                        className="me-2"
                                    />
                                </div>
                                {/* <Button className="preview-btn ms-auto" onClick={handleSendMail} disabled={isLoading}>
                                    <i className="pi pi-send me-1"></i> {isLoading ? 'Sending...' : 'Send'}
                                </Button> */}
                            </Col>
                        </div>
                    </Row>

                    <div className="bg-form form-insidepop">
                        <Row className="mb-2">
                            <Col xl={12}>
                                <Row>
                                    <Col xl={1} className="pe-0 mb-1">
                                        <div className="label-input">
                                            <Dropdown
                                                value={frominputemail}
                                                onChange={(e) => {
                                                    applyFromEmailSelection(e?.value);
                                                }}
                                                options={fromEmail}
                                                optionLabel="name"
                                                placeholder="From"
                                                className="from-width w-full"
                                            />
                                        </div>
                                    </Col>
                                    <Col xl={11} className="mb-1">
                                        <Chips
                                            value={fromemailjobsSelectedTo}
                                            onChange={e => {
                                                const newEntry = e.value[e.value.length - 1];
                                                setFromemailjobsSelectedTo([newEntry]);
                                            }}
                                            disabled
                                            itemTemplate={Fromemailcont}
                                            className="size-mail w-full email-recipient-chips"
                                        />
                                        <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.from}</p>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xl={1} className="pe-0 mb-1">
                                        <div className="label-input">
                                            <Dropdown
                                                value={frominputemail1}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setLinkCantoVisible(true);
                                                }}
                                                optionLabel="name"
                                                placeholder="To"
                                                className="from-width w-full"
                                            />
                                        </div>
                                    </Col>
                                    <Col xl={11} className="mb-1">
                                        <Chips
                                            value={toemailsearchjobs}
                                            onChange={e => setToemailsearchjobs(e.value)}
                                            onClick={() => openRecipientPreview(toemailsearchjobs)}
                                            itemTemplate={item => (
                                                <span onClick={() => openRecipientPreview(toemailsearchjobs)}>{item}</span>
                                            )}
                                            className="size-mail w-full to-recipient-chips"
                                        />
                                        <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.to}</p>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xl={1} className="pe-0 mb-1">
                                        <div className="label-input">
                                            <Dropdown
                                                value={frominputemail2}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setLinkCanccVisible(true);
                                                }}
                                                optionLabel="name"
                                                placeholder="Cc"
                                                className="from-width w-full"
                                            />
                                        </div>
                                    </Col>
                                    <Col xl={11} className="mb-1">
                                        <Chips
                                            value={ccemailjobsSearched}
                                            onChange={e => setCcemailjobsSearched(e.value)}
                                            itemTemplate={Ccemailcont}
                                            className="size-mail w-full email-recipient-chips"
                                        />
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xl={1} className="pe-0 mb-1">
                                        <div className="label-input">
                                            <label htmlFor="interview">Subject:</label>
                                        </div>
                                    </Col>
                                    <Col xl={11} className="mb-1">
                                        <InputText
                                            id="interview"
                                            aria-describedby="username-help"
                                            placeholder=""
                                            className="w-full"
                                            value={subject}
                                            onChange={(e) => {
                                                setSubject(e.target.value);
                                                setFormErrors(prev => ({ ...prev, subject: e.target.value.trim() ? "" : prev.subject }));
                                            }}
                                        />
                                        <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.subject}</p>
                                    </Col>
                                </Row>

                                {attachmentfile.length > 0 && (
                                    <Row className="size-mail mt-2 align-items-baseline">
                                        <Col xl={1}>
                                            <label>Attached:</label>
                                        </Col>
                                        <Col xl={11}>
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                <div
                                                    ref={fileListRef}
                                                    style={{
                                                        display: "flex",
                                                        overflowX: "auto",
                                                        whiteSpace: "nowrap",
                                                        maxWidth: "100%",
                                                        padding: "10px 0px",
                                                        borderRadius: "4px",
                                                    }}
                                                >
                                                    {attachmentfile.map((file, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                marginRight: "15px",
                                                                padding: "5px 10px",
                                                                border: "1px solid #ddd",
                                                                borderRadius: "4px",
                                                                background: "#f9f9f9",
                                                                boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                                                            }}
                                                        >
                                                            <i className="pi pi-file" style={{ marginRight: "5px" }}></i>
                                                            {renderAttachmentName(file, setPreviewFile)}
                                                            <i
                                                                className="pi pi-times"
                                                                style={{
                                                                    marginLeft: "8px",
                                                                    cursor: "pointer",
                                                                    color: "#ff0000",
                                                                }}
                                                                onClick={() => removeFile(file.file_name)}
                                                            ></i>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col xl={12}>
                                <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.body}</p>
                                <Editor
                                    value={jobDescriptions}
                                    onTextChange={e => setJobDescriptions(e.htmlValue)}
                                    className="jobsall-editor"
                                />
                            </Col>
                        </Row>
                    </div>
                </Dialog>
                {/* Jobs Searched End */}


                {/* jobs selected start */}
                {selectedData && selectedData.length > 0 ? (
                    <Dialog
                        header="Send Job Emails to Selected Candidates"
                        visible={jobsselectedpopup}
                        className="interview-popup"
                        style={{ width: "75vw", height: "82%" }}
                        maximizable
                        modal
                        onHide={() => {
                            SetJobsselectedPopup(false);
                            // Reset all form fields
                            setFrominputemail(null);
                            setFromemailjobsSelectedTo([]);
                            setToemailseljobs([]);
                            setCcemailjobssear([]);
                            setSubject(getDefaultEmailSubject());
                            setJobDescriptions(`
          <p>Dear {{candidate_name}}</p>
          <br />
          ${defaultSignature}
        `);
                            setAttachmentfile([]);
                            setSelectedTemplate(null);
                        const defaultSig = signatureOptions.find(signature => signature.is_default) || signatureOptions[0];
                            setSignatureText(defaultSig ? defaultSig.signature_text : (defaultSignature || ""));
    setSelectedSignature(defaultSig || null);
                            setPrivateChecked(false);
                            setFormErrors({});
                            resetSignatureAndTemplate();
                            setSelectedCanto([]);
                            setSelectedCancc([]);
                            if (fileUploadRef.current) {
                                fileUploadRef.current.clear();
                            }
                        }}
                        footer={footerContent}
                    >
                        <Row>
                            <div className="popup-header-bg">
                                <Col lg={12} className="d-flex align-items-center">
                                    {/* <Dropdown
                                        value={selectedTemplate}
                                        onChange={handleTemplateChange}
                                        options={templateOptions}
                                        placeholder="Template"
                                        valueTemplate={selectedEmailTemplate1}
                                        itemTemplate={emailOptionTemplate1}
                                        className="w-full md:w-10rem me-2"
                                        dropdownIcon={opts =>
                                            opts.iconProps["data-pr-overlay-visible"] ? (
                                                <ChevronRightIcon {...opts.iconProps} />
                                            ) : (
                                                <ChevronDownIcon {...opts.iconProps} />
                                            )
                                        }
                                    />
                                    <Dropdown
                                        value={selectedSignature}
                                        onChange={handleSignatureChange}
                                        options={signatureOptions}
                                        optionLabel="label"
                                        placeholder={signatureLoading ? "Loading signatures..." : signatureError || (signatureOptions.length ? "Signature" : "No email signature configured for this company.")}
                                        emptyMessage="No email signature configured for this company."
                                        valueTemplate={selectedSmsTemplate}
                                        itemTemplate={smsOptionTemplate}
                                        className="w-full md:w-10rem me-2 signature-dropdown"
                                        dropdownIcon={opts =>
                                            opts.iconProps["data-pr-overlay-visible"] ? (
                                                <ChevronRightIcon {...opts.iconProps} />
                                            ) : (
                                                <ChevronDownIcon {...opts.iconProps} />
                                            )
                                        }
                                    /> */}
                                    <div className="d-inline-block">
                                        <FileUpload
                                            name="emailAttachments[]"
                                            ref={fileUploadRef}
                                            url={'/api/upload'}
                                            mode="basic"
                                            multiple
                                            accept="image/*,application/pdf"
                                            maxFileSize={10000000}
                                            auto
                                            chooseLabel={<span>Attach File</span>}
                                            customUpload
                                            uploadHandler={onUploadHandler1}
                                            className="me-2"
                                        />
                                    </div>
                                    {/* <Button className="preview-btn ms-auto" onClick={handleSendMail} disabled={isLoading}>
                                        <i className="pi pi-send me-1"></i> {isLoading ? 'Sending...' : 'Send'}
                                    </Button> */}
                                </Col>
                            </div>
                        </Row>

                        <div className="bg-form form-insidepop">
                            <Row className="mb-2">
                                <Col xl={12}>
                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail}
                                                    onChange={(e) => {
                                                        applyFromEmailSelection(e?.value);
                                                    }}
                                                    options={fromEmail}
                                                    optionLabel="name"
                                                    placeholder="From"
                                                    className="from-width w-full"
                                                />

                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={fromemailjobsSelectedTo}
                                                onChange={e => {
                                                    const newEntry = e.value[e.value.length - 1];
                                                    setFromemailjobsSelectedTo([newEntry]);
                                                    setFrominputemail(
                                                        Array.isArray(fromEmail)
                                                            ? fromEmail.find(opt => opt.code === newEntry) || null
                                                            : null
                                                    );
                                                }}
                                                disabled
                                                itemTemplate={Fromemailcont}
                                                className="size-mail w-full email-recipient-chips"
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.from}</p>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail1}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setLinkCantoVisible(true);
                                                    }}
                                                    optionLabel="name"
                                                    placeholder="To"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={toemailSelected}
                                                onChange={e => setToemailSelected(e.value)}
                                                onClick={() => openRecipientPreview(toemailSelected)}
                                                itemTemplate={item => (
                                                    <span onClick={() => openRecipientPreview(toemailSelected)}>{item}</span>
                                                )}
                                                className="size-mail w-full to-recipient-chips"
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.to}</p>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col xl={1} className="pe-0 mb-1">
                                            <div className="label-input">
                                                <Dropdown
                                                    value={frominputemail2}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setLinkCanccVisible(true);
                                                    }}
                                                    optionLabel="name"
                                                    placeholder="Cc"
                                                    className="from-width w-full"
                                                />
                                            </div>
                                        </Col>
                                        <Col xl={11} className="mb-1">
                                            <Chips
                                                value={ccemailjobssear}
                                                onChange={e => setCcemailjobssear(e.value)}
                                                itemTemplate={Ccemailcont}
                                                className="size-mail w-full email-recipient-chips"
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="size-mail">
                                        <Col xl={1}>
                                            <label htmlFor="interview">Subject:</label>
                                        </Col>
                                        <Col xl={11}>
                                            <InputText
                                                id="interview"
                                                aria-describedby="username-help"
                                                placeholder=""
                                                className="w-full"
                                                value={subject}
                                                onChange={(e) => {
                                                    setSubject(e.target.value);
                                                    setFormErrors(prev => ({ ...prev, subject: e.target.value.trim() ? "" : prev.subject }));
                                                }}
                                            />
                                            <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.subject}</p>
                                        </Col>
                                    </Row>

                                    {attachmentfile.length > 0 && (
                                        <Row className="size-mail mt-2 align-items-baseline">
                                            <Col xl={1}>
                                                <label>Attached:</label>
                                            </Col>
                                            <Col xl={11}>
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <div
                                                        ref={fileListRef}
                                                        style={{
                                                            display: "flex",
                                                            overflowX: "auto",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: "100%",
                                                            padding: "10px 0px",
                                                            borderRadius: "4px",
                                                        }}
                                                    >
                                                        {attachmentfile.map((file, index) => (
                                                            <div
                                                                key={index}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    marginRight: "15px",
                                                                    padding: "5px 10px",
                                                                    border: "1px solid #ddd",
                                                                    borderRadius: "4px",
                                                                    background: "#f9f9f9",
                                                                    boxShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                                                                }}
                                                            >
                                                                <i className="pi pi-file" style={{ marginRight: "5px" }}></i>
                                                                {renderAttachmentName(file, setPreviewFile)}
                                                                <i
                                                                    className="pi pi-times"
                                                                    style={{
                                                                        marginLeft: "8px",
                                                                        cursor: "pointer",
                                                                        color: "#ff0000",
                                                                    }}
                                                                    onClick={() => removeFile(file.file_name)}
                                                                ></i>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </Col>
                            </Row>

                            <Row>
                                <Col xl={12}>
                                    <p style={{ color: 'red', fontSize: '13px' }}>{formErrors.body}</p>
                                    <Editor
                                        value={jobDescriptions}
                                        onTextChange={e => setJobDescriptions(e.htmlValue)}
                                        className="jobsall-editor"
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Dialog>
                ) : (
                    <Dialog
                        header="No Candidates Selected"
                        visible={jobsselectedpopup}
                        style={{ width: "30vw" }}
                        modal
                        onHide={() => SetJobsselectedPopup(false)}
                        footer={
                            <div>
                                <Button
                                    label="OK"
                                    icon="pi pi-check"
                                    onClick={() => SetJobsselectedPopup(false)}
                                    className="p-button-primary"
                                />
                            </div>
                        }
                    >
                        <div className="p-2">
                            <p>Please select at least one candidate to send job emails.</p>
                        </div>
                    </Dialog>
                )}
                {/* jobs selected end */}


                {/* candidates popup to */}
                <Dialog
                    header="Select Candidate"
                    visible={linkCantoVisible}
                    onHide={() => {
                        setLinkCantoVisible(false);
                        setSelectedCanto([]); // Clear selected candidates
                        setSearchText(""); // Clear search text
                    }}
                    style={{ width: "40vw" }}
                    breakpoints={{ "960px": "75vw", "641px": "100vw" }}
                    className="cand-details"
                >
                    {/* Search Bar */}
                    <Row>
                        <Col lg={12}>
                            <div className="p-inputgroup mb-3" style={{ position: "relative" }}>
                                <InputText
                                    placeholder="Search candidates..."
                                    value={searchText}
                                    onInput={(e) => setSearchText(e.target.value)}
                                    style={{ paddingRight: "2rem" }}
                                />
                                <i
                                    className="pi pi-search"
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "49%",
                                        transform: "translateY(-50%)",
                                    }}
                                ></i>
                            </div>
                        </Col>
                    </Row>

                    {/* DataTable */}
                    <div id="All_Employees_TableView">
                    <DataTable
                        key={selectedCanto.length} // Force re-render when selectedContacts changes
                        value={filteredCanto(canto)}
                        selection={selectedCanto}
                        onSelectionChange={(e) => setSelectedCanto(e.value)}
                        dataKey="candidate_id"
                        size="small"
                        loading={candidateLoading}
                        lazy
                        paginator
                        first={lazyParams.page * lazyParams.rows}
                        rows={lazyParams.rows}
                        totalRecords={totalRecords}
                        onPage={onPageChange}
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} to {last} of {totalRecords}"
                        rowsPerPageOptions={[10, 20, 50, totalRecords]}
                        emptyMessage={candidateLoading ? "Loading candidates..." : "No candidates found."}
                    >
                        <Column selectionMode="multiple" style={{ width: "3em" }} />
                        <Column
                            header="Candidate Name"
                            body={(rowData) => `${rowData.first_name || ""} ${rowData.last_name || ""}`}
                        />
                        <Column field="job_title" header="Designation" />
                        <Column field="email" header="Email" />
                    </DataTable>
                    </div>

                    {/* Actions */}
                    <div className="d-flex mt-3 justify-content-center">
                        <button
                            type="button"
                            className="btn btn-primary me-2 btn-main"
                            onClick={handleSaveCanto}
                        >
                            Ok
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-main"
                            onClick={handleRefresh1to}
                        >
                            Refresh
                        </button>
                    </div>
                </Dialog>


                {/* candidates popup cc */}
                <Dialog
                    header="Select Candidate"
                    visible={linkCanccVisible}
                    onHide={() => {
                        setLinkCanccVisible(false);
                        setSelectedCancc([]); // Clear selected candidates
                        setSearchText(""); // Clear search text
                    }}
                    style={{ width: "40vw" }}
                    breakpoints={{ "960px": "75vw", "641px": "100vw" }}
                    className="cand-details"
                >
                    {/* Search Bar */}
                    <Row>
                        <Col lg={12}>
                            <div className="p-inputgroup mb-3" style={{ position: "relative" }}>
                                <InputText
                                    placeholder="Search candidates..."
                                    value={searchText}
                                    onInput={(e) => setSearchText(e.target.value)}
                                    style={{ paddingRight: "2rem" }}
                                />
                                <i
                                    className="pi pi-search"
                                    style={{
                                        position: "absolute",
                                        right: "10px",
                                        top: "49%",
                                        transform: "translateY(-50%)",
                                    }}
                                ></i>
                            </div>
                        </Col>
                    </Row>

                    {/* DataTable */}
                    <div id="All_Employees_TableView">
                    <DataTable
                        key={selectedCancc.length} // Force re-render when selectedContacts changes
                        value={filteredCanto(canto)}
                        selection={selectedCancc}
                        onSelectionChange={(e) => setSelectedCancc(e.value)}
                        dataKey="candidate_id"
                        size="small"
                        loading={candidateLoading}
                        lazy
                        paginator
                        first={lazyParams.page * lazyParams.rows}
                        rows={lazyParams.rows}
                        totalRecords={totalRecords}
                        onPage={onPageChange}
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} to {last} of {totalRecords}"
                        rowsPerPageOptions={[10, 20, 50, totalRecords]}
                        emptyMessage={candidateLoading ? "Loading candidates..." : "No candidates found."}
                    >
                        <Column selectionMode="multiple" style={{ width: "3em" }} />
                        <Column
                            header="Candidate Name"
                            body={(rowData) => `${rowData.first_name || ""} ${rowData.last_name || ""}`}
                        />
                        <Column field="designation" header="Designation" />
                        <Column field="email" header="Email" />
                    </DataTable>
                    </div>
                    

                    {/* Actions */}
                    <div className="d-flex mt-3 justify-content-center">
                        <button
                            type="button"
                            className="btn btn-primary me-2 btn-main"
                            onClick={handleSaveCancc}
                        >
                            Ok
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-main"
                            onClick={handleRefresh1cc}
                        >
                            Refresh
                        </button>
                    </div>
                </Dialog>

            </div>
<Dialog
    header="Selected Recipients"
    visible={recipientPreviewVisible}
    style={{ width: "520px" }}
    modal
    onHide={() => setRecipientPreviewVisible(false)}
>
    <div style={{ maxHeight: 320, overflowY: "auto" }}>
        <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
                <tr>
                    <th style={{ width: 60 }}>S.No</th>
                    <th>Candidate Name</th>
                    <th>Email</th>
                </tr>
            </thead>
            <tbody>
                {recipientPreviewList.map(recipient => (
                    <tr key={`${recipient.id}-${recipient.email}`}>
                        <td>{recipient.id}</td>
                        <td>{recipient.name}</td>
                        <td>{recipient.email}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
</Dialog>
<Dialog
    header="No Subject"
    visible={showNoSubjectConfirm}
    style={{ width: "400px" }}
    modal
    onHide={handleCancelSend}
    footer={
        <div>
            <Button label="Yes, Send" className="btn btn-primary me-2" onClick={handleConfirmSend} />
            <Button label="Cancel" className="btn btn-secondary" onClick={handleCancelSend} />
        </div>
    }
>
    <p>Do you want to send this email without a subject?</p>
</Dialog>
{(() => {
    const recipients = getRecipientsForConfirm();
    const fromAddress = fromemailjobsSelectedTo?.[0] || frominputemail?.name || "--";

    return (
        <Dialog
            header="Confirm Send"
            visible={showSendConfirmModal}
            style={{ width: "500px" }}
            modal
            onHide={() => setShowSendConfirmModal(false)}
            footer={
                <div className="d-flex align-items-center justify-content-center gap-3">
                    <Button
                        label="Cancel"
                        className="btn btn-secondary"
                        onClick={() => setShowSendConfirmModal(false)}
                    />
                    <Button
                        label={isLoading ? "Sending..." : "Confirm & Send"}
                        className="btn btn-primary me-2"
                        disabled={isLoading}
                        onClick={() => {
                            setShowSendConfirmModal(false);
                            sendNewMail();
                        }}
                    />
                </div>
            }
        >
            <p className="mb-2">
                <strong>From:</strong> {fromAddress}
            </p>

            <p className="mb-2">
                <strong>Sending to {recipients.length} candidate{recipients.length > 1 ? "s" : ""}:</strong>
            </p>

            {recipients.length > 1 ? (
                <div style={{ maxHeight: 250, overflowY: "auto" }}>
                    <table className="table table-bordered table-sm mb-0">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: 40 }}>S.No</th>
                                <th>Candidate Name</th>
                                <th>Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recipients.map((r, idx) => (
                                <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td>{r.name}</td>
                                    <td>{r.email}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p>
                    {recipients[0]?.name && recipients[0].name !== "--"
                        ? `${recipients[0].name} (${recipients[0].email})`
                        : recipients[0]?.email || "--"}
                </p>
            )}
        </Dialog>
    );
})()}
{previewFile && (() => {
  const isUrl = typeof previewFile.file_data === 'string' &&
    (previewFile.file_data.startsWith('http://') || previewFile.file_data.startsWith('https://'));

  const fileName = previewFile.file_name || "";
  const ext = fileName.split('.').pop().toLowerCase();

  const isPdf = ext === 'pdf' ||
    (typeof previewFile.file_data === 'string' && previewFile.file_data.startsWith('data:application/pdf'));

  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext) ||
    (typeof previewFile.file_data === 'string' && previewFile.file_data.startsWith('data:image/'));

  const isDocx = ['doc', 'docx'].includes(ext);
  let previewSrc = "";
  if (isUrl) {
    if (isDocx) {
      previewSrc = `https://docs.google.com/gview?url=${encodeURIComponent(previewFile.file_data)}&embedded=true`;
    } else {
      previewSrc = previewFile.file_data;
    }
  } else {
    previewSrc = previewFile.file_data;
  }

  return (
    <Dialog
      header={
        <div className="d-flex align-items-center justify-content-between w-100 pe-3">
          <span style={{ fontSize: 15, fontWeight: 600 }}>{fileName}</span>
        </div>
      }
      visible={!!previewFile}
      onHide={() => setPreviewFile(null)}
      style={{ width: "80vw", maxWidth: 1100 }}
      contentStyle={{ padding: 0, height: "75vh" }}
      modal
      dismissableMask
    >
      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9" }}>
        {isImage ? (
          <img
            src={previewSrc}
            alt={fileName}
            style={{ maxWidth: "100%", maxHeight: "73vh", objectFit: "contain", borderRadius: 6 }}
          />
        ) : isPdf || isDocx || isUrl ? (
          <iframe
            src={previewSrc}
            title={fileName}
            style={{ width: "100%", height: "73vh", border: "none" }}
          />
        ) : (
          <div className="text-center p-5">
            <i className="pi pi-file" style={{ fontSize: 48, color: "#94a3b8" }} />
            <p className="mt-3 text-muted" style={{ fontSize: 14 }}>
              Preview not available for this file type.
            </p>
            <a
              href={previewFile.file_data}
              download={fileName}
              className="btn btn-primary mt-2"
            >
              <i className="pi pi-download me-2" />
              Download File
            </a>
          </div>
        )}
      </div>
    </Dialog>
  );
})()}

        </React.Fragment>

    )
}

export default EmailAC;
